import type { IsoDate } from "@/lib/content-date";

export type BlogAuthor = {
  name: string;
  initials: string;
  /** CSS gradient for the avatar circle. */
  avatar: string;
  /** One line shown under the byline on the article page. */
  bio: string;
};

export type BlogCategory = "Playbooks" | "Product" | "Guides" | "Company";

/** A block of article body content. Paragraph/bullet text supports `**bold**`. */
export type ArticleBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "quote"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "image"; label: string };

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  /** Describes the article's editorial cover artwork. */
  imageAlt: string;
  category: BlogCategory;
  author: BlogAuthor;
  /** Publication date, ISO 8601. Formatted for display at render time. */
  date: IsoDate;
  /**
   * When the post was last materially revised, if ever. Feeds the Article's
   * `dateModified`; leave it unset rather than bumping it for a typo, because a
   * date that moves without the content changing is the signal Google learns
   * to ignore.
   */
  updated?: IsoDate;
  /** Display read time, e.g. "5 min". */
  read: string;
  /** OKLCH hue for the striped cover placeholder. */
  hue: number;
  body: ArticleBlock[];
};

/**
 * The byline.
 *
 * This file previously carried five invented authors — "Ava Klein, Co-founder
 * & CEO", "Rae Tanaka, Head of Design" and so on — none of whom exist. Google's
 * quality guidance leans directly on who wrote a page and why they are worth
 * trusting, so shipping fabricated author identities is both an E-E-A-T
 * liability and simply dishonest. Posts are credited to PostDaily itself, the
 * organisation named in the Article JSON-LD and on `/about`; the owner asked
 * for no personal names on the site (2026-09-27).
 */
const POSTDAILY: BlogAuthor = {
  name: "PostDaily",
  initials: "PD",
  avatar: "linear-gradient(135deg,oklch(0.62 0.16 268),oklch(0.6 0.16 285))",
  bio: "Written by the people who build PostDaily's publishing engine.",
};

/**
 * The first article written for search rather than for the launch mock.
 *
 * Targets "how to schedule posts on mastodon" — the softest SERP in this
 * category, where the current results are small tools and a Medium post. The
 * angle every one of them misses: Mastodon CAN schedule, the scheduler is in
 * the API, and the official apps simply do not expose it. Leading with that
 * costs us nothing and is the reason a fediverse reader might trust the rest
 * of the page, which is the whole game with this audience.
 *
 * Facts here were checked against docs.joinmastodon.org and fedi.tips on
 * 2026-08-31. If Mastodon ships scheduling in the official web app, this
 * article and the /mastodon-scheduler page both need rewriting.
 */
const MASTODON_SCHEDULING: BlogPost = {
  slug: "how-to-schedule-mastodon-posts",
  title: "How to schedule Mastodon posts (and when you don't need a tool)",
  excerpt:
    "Mastodon has a scheduler. It is just not where you would look for it. Here is how to reach it for free, and the point at which something more is worth it.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "7 min read",
  imageAlt:
    "A post card moving through a calendar to several independent community servers",
  hue: 265,
  body: [
    {
      type: "p",
      text: "If you have gone looking for a schedule button in Mastodon and come away thinking it does not exist, you are not missing anything obvious. **Mastodon can schedule posts, and the official web app cannot.** Both of those are true at once, which is why this is confusing.",
    },
    {
      type: "p",
      text: "This guide covers where the scheduler actually lives, how to use it for free, the limits nobody mentions until you hit them, and the honest point at which a separate tool starts earning its keep.",
    },
    { type: "h2", text: "The short answer" },
    {
      type: "p",
      text: "Scheduling is built into Mastodon's API. When something posts to your server it can pass a `scheduled_at` time, and your server holds the post and publishes it for you. There are API endpoints to list what you have scheduled, change a time, and cancel one.",
    },
    {
      type: "p",
      text: "What there is not, at time of writing, is a way to reach any of that from the official web interface or the official mobile apps. The feature is real and finished; it just has no button. So to schedule a Mastodon post you use a **third-party client** that exposes what the API already does.",
    },
    { type: "h2", text: "Apps that can schedule a Mastodon post" },
    {
      type: "p",
      text: "All of these talk to your own server, so a post scheduled in any of them is held by your instance rather than by the app's company. Pick whichever suits the device you post from.",
    },
    {
      type: "ul",
      items: [
        "**Android** — Tusky, Fedilab, Moshidon, Subway Tooter",
        "**iPhone and iPad** — Mona",
        "**Windows and Linux desktop** — Tuba",
        "**In a browser** — Phanpy, FediPlan",
      ],
    },
    {
      type: "p",
      text: "The flow is the same everywhere: write the post, look for a clock or calendar icon in the compose screen, pick a date and time, and send. It moves into a scheduled list you can edit or cancel until it goes out.",
    },
    {
      type: "p",
      text: "Some instances also add their own schedule button to the compose box. If yours does, use it — it is the same underlying feature. If you end up wanting more than one account in one place, that is what the [Mastodon scheduler](/mastodon-scheduler) is for.",
    },
    { type: "h2", text: "Four limits worth knowing before you rely on it" },
    {
      type: "p",
      text: "**A scheduled post has to be at least five minutes in the future.** This one catches people out constantly. If you try to schedule something for two minutes from now, the server rejects it. Round up.",
    },
    {
      type: "p",
      text: "**Scheduled posts live on the server you created them on.** If you migrate your account to a different instance later, anything you had queued does not come with you. Migration moves followers, not drafts. Before you move, either let the queue drain or write the posts down somewhere else.",
    },
    {
      type: "p",
      text: "**The 500-character limit is a default, not a rule.** Mastodon ships with 500, and plenty of instances raise it — some substantially. A post that is fine on one server can be rejected by another, which matters the moment you post to more than one. Mastodon also counts generously: every link counts as 23 characters no matter how long the URL is, and the domain part of a mention does not count at all — [the full rules are here](/blog/mastodon-character-limit).",
    },
    {
      type: "p",
      text: "**Your scheduled post keeps the settings you gave it.** Visibility and content warnings are stored with the post, not applied at publish time. If you schedule something public that should have been followers-only, you have to cancel and rewrite it rather than change it after the fact — so check the visibility dropdown before you send, not after.",
    },
    { type: "h2", text: "When a client is genuinely enough" },
    {
      type: "p",
      text: "I want to be straight about this, because most articles on this subject are written by scheduling companies and skip it: if you post to **one Mastodon account**, a few times a week, from **one device**, a third-party client does the whole job for free. You do not need a scheduling tool, and you should not pay for one.",
    },
    {
      type: "quote",
      text: "The honest test is not whether you want to schedule a post. It is whether you are trying to run a publishing routine.",
    },
    { type: "h2", text: "When it stops being enough" },
    {
      type: "p",
      text: "The client approach breaks down in fairly specific places, and you will know when you hit them:",
    },
    {
      type: "ul",
      items: [
        "**You cannot see the shape of your month.** A scheduled list is a list. It tells you what is queued, not whether you have three posts stacked on Tuesday and nothing for a week after.",
        '**You are picking every time by hand.** Deciding a date and time for every post is a small tax that gets heavy. What you usually want is to say "weekday mornings and Sunday evening" once, and have posts drop into the next free slot.',
        "**You post to more than one account or server.** Clients are account-shaped. Two accounts on two instances means two apps, or a lot of logging in and out.",
        "**You are not only on Mastodon.** If the same thought goes to Bluesky and Mastodon, writing it twice in two apps is the actual work, not the scheduling.",
        "**Someone else has to approve it.** Client work, a co-founder, a comms review — none of that fits a compose box.",
        "**You want to know what worked.** Mastodon deliberately keeps analytics thin. A client will not tell you which posts landed.",
      ],
    },
    { type: "h2", text: "What a scheduling tool adds" },
    {
      type: "p",
      text: "This is the part where I am obviously not neutral, so treat it as a description rather than a pitch. A tool like PostDaily sits on the same API a client does. What it adds is the layer above a single post: a calendar you can plan a month on, a posting queue that fills itself from times you set once per channel, several accounts and several servers in one composer, per-post analytics, and a review link you can send to someone who does not have an account.",
    },
    {
      type: "p",
      text: "It also handles the counting properly — the same weighted rules Mastodon uses, so the editor and the server never disagree about whether a post fits — and it retries when a publish fails rather than dropping it quietly.",
    },
    {
      type: "p",
      text: 'None of that is worth anything if your problem is "I would like this one post to go out tomorrow morning". Install Tusky.',
    },
    { type: "h2", text: "A reasonable way to decide" },
    {
      type: "p",
      text: "Start with a client. It is free, it uses the feature Mastodon already built, and it will tell you within a fortnight whether scheduling is actually the thing you needed. If you find yourself keeping a separate list of what to post when, or opening two apps to say the same thing twice, that is the signal — and it will be obvious rather than a judgement call.",
    },
    {
      type: "p",
      text: "Either way, the scheduler is there. It is just hidden, and nobody tells you. And if your next question is what time to schedule for, [we looked into that too](/blog/best-time-to-post-on-mastodon) — the answer is less exciting than the genre suggests.",
    },
  ],
};

/**
 * Article two. Targets "how to schedule bluesky posts".
 *
 * Harder SERP than the Mastodon one — Buffer, Hootsuite and Sprout all publish
 * here — but a one-person blog currently outranks them, so the bar is content
 * quality rather than domain authority. The gap in every competing result is
 * the app password: they cover it in a sentence and move on, while it is the
 * single thing that stops people completing the setup. Handled properly here
 * and in depth in the companion post.
 *
 * Checked 2026-08-31: Bluesky has no native scheduler on web or mobile, and
 * the open feature request carries no committed timeline. If that ships, this
 * article needs rewriting.
 */
const BLUESKY_SCHEDULING: BlogPost = {
  slug: "how-to-schedule-bluesky-posts",
  title: "How to schedule Bluesky posts in 2026",
  excerpt:
    "Bluesky still has no scheduler of its own. Here is how to get posts going out on a schedule, including the app password step that stops most people.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "8 min read",
  imageAlt:
    "Blue post cards flowing from an abstract feather into a calendar and clock",
  hue: 210,
  body: [
    {
      type: "p",
      text: "Bluesky has no built-in scheduling. Not on the website, not in the mobile app. There is an open feature request and no committed timeline, so if you want a post to go out at 8am on Thursday without being awake for it, you need something outside Bluesky.",
    },
    {
      type: "p",
      text: "That part is simple. The part that stops people is the next screen, where a tool asks for an **app password** and it is not obvious whether that is a normal thing to hand over. So this guide covers the whole path, including that.",
    },
    { type: "h2", text: "What you need" },
    {
      type: "p",
      text: "Two things: a tool that supports Bluesky, and an app password from your Bluesky account. No developer account, no API key, no waiting for approval.",
    },
    {
      type: "p",
      text: "Unlike most networks, Bluesky does not use a redirect-and-approve flow today. Instead of bouncing you to Bluesky to click **Allow**, the tool asks for your handle and an app password you generate yourself. It feels less familiar than an OAuth screen, and it is genuinely different, so it is worth understanding rather than clicking through.",
    },
    { type: "h2", text: "Creating an app password" },
    {
      type: "p",
      text: "In the Bluesky app or website, open **Settings → Privacy and Security → App Passwords** and add one. Bluesky moves its settings around occasionally; if it is not there, search the settings for *app password*.",
    },
    {
      type: "ul",
      items: [
        "**Name it after the tool.** You will end up with several. A list of unnamed passwords is a list you cannot safely prune later.",
        "**Leave the direct messages checkbox off** unless the tool genuinely needs to read your DMs. A scheduler does not.",
        "**Copy it immediately.** It is shown once, as four groups of four characters. Paste it exactly as given, hyphens included.",
      ],
    },
    {
      type: "p",
      text: "The short version of why this is safe: an app password cannot delete your account, cannot migrate it to another server, and cannot create more app passwords. You can revoke any one of them at any time without touching your real password. If you want the longer version — including how to judge whether a tool deserves one — I wrote [a separate piece on exactly that](/blog/bluesky-app-passwords-explained).",
    },
    { type: "h2", text: "Writing a post Bluesky will accept" },
    {
      type: "p",
      text: "Bluesky allows **300 characters** and up to four images. The 300 is where it gets interesting, because Bluesky does not count the way a naive text box counts.",
    },
    {
      type: "p",
      text: "It counts **grapheme clusters** — what a human sees as one character. A family emoji is one character to Bluesky even though it is assembled from several code points joined together. An editor that counts the underlying units will tell you a post is far longer than it is, and reject something Bluesky would have accepted.",
    },
    {
      type: "p",
      text: "There is also a separate byte ceiling underneath the 300. For a long post carrying a lot of emoji or non-Latin script, that byte limit is usually what you hit first, which is why a post can be under 300 characters and still be refused.",
    },
    {
      type: "p",
      text: "Practical version: if a tool tells you a post fits and Bluesky then rejects it, the tool is counting wrong (there is a [breakdown of how each network counts](/help/character-limits-by-network) if you want the detail). That is a real difference between schedulers and worth testing with one emoji-heavy post before you trust one with a month of content.",
    },
    { type: "h2", text: "Threads" },
    {
      type: "p",
      text: "A Bluesky thread is a chain of posts, each a reply to the one before. Scheduling one means the whole chain has to publish in order, with each segment's images attached to the right post.",
    },
    {
      type: "p",
      text: "Two approaches exist and they are not equivalent. Some tools take one long block of text and split it automatically at 300 characters, which breaks sentences in unfortunate places and gives you no way to attach an image to segment three. Others let you write each segment yourself. If threads matter to you, check which one you are getting before committing — and [there is more on writing them well](/blog/bluesky-threads-guide).",
    },
    { type: "h2", text: "Why timing matters more on Bluesky" },
    {
      type: "p",
      text: "This is the part most scheduling advice gets wrong by copying itself across networks. Bluesky's default Following feed is **chronological**. There is no ranking model deciding to resurface a good post six hours later because it is performing well.",
    },
    {
      type: "quote",
      text: "On an algorithmic network a badly timed post can still be rescued. On a chronological one, if your followers were not looking, the post effectively did not happen.",
    },
    {
      type: "p",
      text: "That makes scheduling a different job here — and it is why the [Bluesky scheduling page](/bluesky-scheduler) leans on per-channel time grids. It is not about squeezing more posts out — it is about landing them in the windows your followers are actually reading. Which also means generic best-time advice is close to useless: what matters is when *your* followers are awake, and that is worth watching for a couple of weeks before settling into a pattern.",
    },
    { type: "h2", text: "A sensible first week" },
    {
      type: "ul",
      items: [
        "**Schedule one post**, not thirty. Confirm it actually goes out and looks right.",
        "**Try one emoji-heavy post** to see whether the character counter and Bluesky agree.",
        "**Vary the times** deliberately for a week or two rather than picking a slot on day one.",
        "**Check what happens when something fails.** A connection expires eventually. Find out now whether your tool tells you, or whether posts just quietly stop.",
      ],
    },
    {
      type: "p",
      text: "That last one sounds pessimistic and is the most useful test on the list. Every scheduler works when nothing goes wrong.",
    },
  ],
};

/**
 * Article four. Targets "bluesky app password" and the anxious long-tail
 * around it — "is it safe to give my bluesky app password".
 *
 * A security question with weak answers and direct commercial relevance:
 * someone searching this is mid-signup for a tool and deciding whether to
 * continue. It only works if it reads as neutral, so it explains how to judge
 * ANY tool rather than arguing for this one.
 *
 * Checked 2026-08-31 against Bluesky's own documentation: app passwords cannot
 * delete an account, migrate it, or create further app passwords; DM access is
 * an explicit opt-in checkbox; Bluesky has stated OAuth is expected to replace
 * app passwords over time.
 */
const BLUESKY_APP_PASSWORDS: BlogPost = {
  slug: "bluesky-app-passwords-explained",
  title:
    "Bluesky app passwords: what they are, and whether it is safe to give one out",
  excerpt:
    "A tool is asking for an app password and you want to know what you are handing over. Exactly what one can and cannot do, and how to judge whether to give it.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt: "A small blue key unlocking one app card beside a protected vault",
  hue: 195,
  body: [
    {
      type: "p",
      text: "You have found a Bluesky tool you want to try, and it is asking for an app password. Reasonably, you have stopped to wonder what that actually gives it.",
    },
    {
      type: "p",
      text: "Short answer: an app password is a **separate, revocable credential** that does most of what your real password does, minus the things you would never want a third party doing. It is a deliberately safer way to connect a tool than handing over your account password, which is what the alternative would be.",
    },
    {
      type: "p",
      text: "The longer answer is worth five minutes, because *safer* is not the same as *safe*, and the difference is entirely about which tool you give it to.",
    },
    { type: "h2", text: "What an app password is" },
    {
      type: "p",
      text: "It is a credential you generate inside Bluesky, for one purpose, that you can destroy without consequence. You can have several — one per tool — and revoking one has no effect on the others or on your ability to log in normally.",
    },
    {
      type: "p",
      text: "Bluesky shows it once, as four groups of four characters. You paste it into the tool and never see it again, which is the point: it is not a password you remember, it is a key you issue.",
    },
    { type: "h2", text: "What it cannot do — the important part" },
    {
      type: "p",
      text: "This is the bit that actually answers the safety question. Compared to your real password, an app password is blocked from the destructive actions:",
    },
    {
      type: "ul",
      items: [
        "**It cannot delete your account.**",
        "**It cannot migrate your account** to a different server.",
        "**It cannot create more app passwords**, so a compromised one cannot quietly mint others.",
      ],
    },
    {
      type: "p",
      text: "So the worst realistic case is a tool that posts things you did not write, or reads things you would rather it did not. That is genuinely bad and it is also recoverable in one click, which is not true of a leaked account password.",
    },
    { type: "h2", text: "The direct messages checkbox" },
    {
      type: "p",
      text: "When you create one, Bluesky offers a checkbox to allow access to your direct messages. It is **off by default and should usually stay off.**",
    },
    {
      type: "p",
      text: "A scheduling tool has no reason to read your DMs. Neither does an analytics tool, a feed builder, or a backup tool. If something asks you to switch it on, it should be obvious from what the tool does why it needs it — an inbox client, say. If it is not obvious, that is a reason to stop and ask, not a box to tick to make the error go away.",
    },
    { type: "h2", text: "How to create one" },
    {
      type: "p",
      text: "In Bluesky, open **Settings → Privacy and Security → App Passwords** and add one. If the menu has moved, search the settings for *app password*.",
    },
    {
      type: "p",
      text: "Name it after the tool you are about to connect. This sounds fussy and pays off the first time you look at a list of five and try to work out which one belongs to the thing you stopped using in March.",
    },
    { type: "h2", text: "How to revoke one" },
    {
      type: "p",
      text: "Same screen. Delete the entry and that credential stops working immediately. The tool loses access, everything else keeps working, and you do not have to change your real password or sign out anywhere.",
    },
    {
      type: "p",
      text: "Worth doing whenever you stop using a tool, and worth doing immediately if a tool you use is ever reported as breached. It costs nothing.",
    },
    { type: "h2", text: "How to judge whether a tool deserves one" },
    {
      type: "p",
      text: "The credential is safe by design. The question is the tool. A few things worth checking, none of which are specific to any product:",
    },
    {
      type: "ul",
      items: [
        "**Does it ask for DM access it does not need?** The clearest single signal.",
        "**Does it explain what it will do with the connection**, or does it just want the field filled in?",
        "**Is there a real company or person behind it**, with a way to contact them?",
        "**Can you disconnect from inside the tool**, or does it assume you will never leave?",
        "**Does it ask for your account password instead?** Anything that does should be closed immediately. There is no legitimate reason for it.",
      ],
    },
    {
      type: "p",
      text: "That last one is the only hard rule here. Everything else is judgement.",
    },
    { type: "h2", text: "What is replacing app passwords" },
    {
      type: "p",
      text: "Bluesky has said OAuth is expected to become the primary way apps authenticate, replacing app passwords over time. That is the familiar flow you get on other networks: you are sent to Bluesky, you approve specific permissions, and the tool never sees a credential at all.",
    },
    {
      type: "p",
      text: "It is better, and it is coming. Until it lands broadly, app passwords are how this works — and understanding them properly is more useful than avoiding every tool until the migration is finished.",
    },
    {
      type: "p",
      text: "If you are here because you are setting up scheduling specifically, the practical walkthrough is in [the companion guide to this one](/blog/how-to-schedule-bluesky-posts).",
    },
  ],
};

/** #5 — reference query, weak results, pairs with the help-centre page. */
const MASTODON_CHARACTER_LIMIT: BlogPost = {
  slug: "mastodon-character-limit",
  title: "Why your Mastodon server's character limit isn't 500",
  excerpt:
    "500 is the default, not the rule. Here is how Mastodon counts, which servers change it, and what happens when you post across several.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "5 min read",
  imageAlt:
    "Three server stacks holding message cards of different lengths beside a ruler",
  hue: 285,
  body: [
    {
      type: "p",
      text: "Mastodon's character limit is **500** — except when it is not. Unlike a centralised network, where one company sets one number, every Mastodon server chooses its own. 500 is what the software ships with, and plenty of admins raise it.",
    },
    {
      type: "p",
      text: "That is fine until you post to more than one server, or use a tool that assumes a single limit. Then it becomes the reason a post that looked fine gets rejected.",
    },
    { type: "h2", text: "How Mastodon actually counts" },
    {
      type: "p",
      text: "Two rules make Mastodon more generous than a plain character count suggests, and both are worth knowing because they change how you write.",
    },
    {
      type: "p",
      text: "**Every link counts as 23 characters.** It does not matter whether the URL is 20 characters or 200 — it costs you 23. Shortening a link before posting to Mastodon achieves nothing except making it uglier and harder to trust.",
    },
    {
      type: "p",
      text: "**The domain part of a mention does not count.** Writing @someone@a-very-long-instance.example costs the same as @someone. Conversations across servers would be unusable otherwise.",
    },
    {
      type: "p",
      text: "So a post packed with links and mentions has far more room than any naive counter will tell you. If a tool is counting raw characters, it is under-reporting how much you can say.",
    },
    { type: "h2", text: "The content warning is separate" },
    {
      type: "p",
      text: "The content warning field has its own allowance and does not eat into the post body. Which is a small kindness, and one reason CWs are used so freely on Mastodon compared to how content notes work elsewhere.",
    },
    { type: "h2", text: "Servers that change the limit" },
    {
      type: "p",
      text: "Admins can raise the limit, and some do — a few substantially. There is no central list, but every server publishes its own numbers through its API, so [you can look yours up](/tools/mastodon-limits) in seconds. Know your own server's number, and treat 500 as the safe floor when posting anywhere else.",
    },
    {
      type: "quote",
      text: "If you post to one server, use its limit. If you post to several, write to 500 and you will never be surprised.",
    },
    {
      type: "p",
      text: "Most scheduling tools hold every Mastodon post to 500 rather than ask each server. [PostDaily reads your server's own limit](/mastodon-scheduler) when you connect and checks it daily, so a post is held to what your server actually allows, and falls back to 500 only when a server doesn't say.",
    },
    { type: "h2", text: "What happens if you go over" },
    {
      type: "p",
      text: "Your server rejects the post. It is not truncated, not posted partially, not queued — refused. If it happened through a scheduling tool, whether you find out depends entirely on whether that tool surfaces the error or swallows it, which is worth testing before you rely on one.",
    },
    { type: "h2", text: "Writing longer" },
    {
      type: "ul",
      items: [
        "**Thread it.** A reply chain is native to Mastodon and reads better than a wall of text anyway.",
        "**Put the detail behind a link.** A blog post with a link costs you 23 characters and gives readers the option.",
        "**Use the content warning as a headline.** A short CW plus a longer body is a well-established Mastodon pattern, not a workaround.",
      ],
    },
    { type: "h2", text: "How to find your own server's limit" },
    {
      type: "p",
      text: "The reliable way is to look: open the compose box and start typing. The counter reflects your server's actual configured limit, whatever it is, and it is the only source that cannot be out of date.",
    },
    {
      type: "p",
      text: "Some servers also state it in their About page alongside the rules, which is worth checking if you are choosing where to join and long posts matter to you.",
    },
    { type: "h2", text: "Why servers raise it, and why most do not" },
    {
      type: "p",
      text: "Admins who raise the limit usually do it for a community that writes essays — academic, literary, technical. Admins who leave it alone tend to argue that the constraint is what keeps timelines readable, and that people who want long-form should link to it.",
    },
    {
      type: "p",
      text: "Both positions are defensible and neither is going to win, which is precisely why the limit is a per-server setting rather than a platform decision. It is a small illustration of [what federation actually means in practice](/blog/what-is-the-fediverse): the software ships a default, and the person running each server decides whether to keep it.",
    },
    { type: "h2", text: "A note on people reading you elsewhere" },
    {
      type: "p",
      text: "A post written on a server with a raised limit still federates to servers running the default. It does not get truncated — receiving servers display what they are sent — but it will look enormous in a timeline of 500-character posts.",
    },
    {
      type: "p",
      text: "Worth remembering if your server allows 5,000 and your audience is mostly elsewhere. The limit you write to is a courtesy question as much as a technical one.",
    },
    {
      type: "p",
      text: "If you want the same breakdown for every other network, there is [a reference for that](/help/character-limits-by-network).",
    },
  ],
};

/** #7 — a real unsolved workflow with almost no competition. */
const CROSS_POST_BLUESKY_MASTODON: BlogPost = {
  slug: "cross-post-bluesky-mastodon",
  title: "How to cross-post between Bluesky and Mastodon",
  excerpt:
    "Three ways to be on both without writing everything twice — bridging, scheduling to both, or posting natively. What each costs you.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "7 min read",
  imageAlt:
    "One post branching across two bridges to blue and violet communities",
  hue: 240,
  body: [
    {
      type: "p",
      text: "Bluesky and Mastodon are built on different protocols that cannot talk to each other directly. Bluesky uses the AT Protocol; Mastodon uses [ActivityPub](/blog/what-is-the-fediverse). If you want an audience on both, something has to bridge the gap — and your three options have genuinely different trade-offs.",
    },
    { type: "h2", text: "Option 1: bridge the accounts" },
    {
      type: "p",
      text: "**Bridgy Fed** sits between the two protocols and translates. It is opt-in on both sides, free, and once enabled your posts appear on the other network without you doing anything. People over there can follow, reply and boost, and you see it back.",
    },
    {
      type: "p",
      text: "It is the least effort by a distance. It also has edges worth knowing before you rely on it:",
    },
    {
      type: "ul",
      items: [
        "**Edits do not propagate.** Mastodon supports editing a post; Bluesky does not. Fix a typo on Mastodon and the bridged copy keeps the typo.",
        "**Direct messages do not cross.** The two protocols handle private messaging differently enough that bridging them is not supported.",
        "**Quote posts from Mastodon do not bridge**, because the two sides implement quoting differently.",
        "**Everything goes.** A bridge is all-or-nothing. You cannot send some posts and hold others back.",
      ],
    },
    {
      type: "p",
      text: "That last one is the real decision. If your Bluesky voice and your Mastodon voice are the same, a bridge is excellent. If they are not — and for a lot of people they genuinely are not — it is the wrong tool.",
    },
    { type: "h2", text: "Option 2: schedule to both" },
    {
      type: "p",
      text: "Write once in a tool that publishes to both networks natively. Each post is a real native post on each side: no bridge, no proxy account, no translation layer, and full control over which post goes where.",
    },
    {
      type: "p",
      text: "The thing to check is whether the tool respects each network properly, because they are not the same and pretending they are produces bad posts:",
    },
    {
      type: "ul",
      items: [
        "**Different limits.** 300 on Bluesky, 500 on Mastodon by default. A tool that counts to one number is wrong on the other.",
        "**Different counting rules.** Bluesky counts grapheme clusters; Mastodon counts links as 23 and ignores mention domains.",
        "**Content warnings are Mastodon-only.** There is no Bluesky equivalent, and a tool that drops the CW silently is worse than one that refuses.",
        "**Per-network text.** The ability to send a shorter version to Bluesky and a longer one to Mastodon, from one composer, is the thing that makes this approach better than posting twice.",
      ],
    },
    {
      type: "p",
      text: "This is what [PostDaily does](/mastodon-scheduler), so treat that as disclosure rather than a recommendation — but the checklist above is worth applying to whatever you pick.",
    },
    { type: "h2", text: "Option 3: post to each natively" },
    {
      type: "p",
      text: "Open both apps and write twice. Genuinely the best output, genuinely the most work, and it does not scale past a few posts a week.",
    },
    {
      type: "p",
      text: "Worth saying plainly because the other two options are often sold as strictly better and they are not. Writing for a specific audience beats syndicating to a general one, every time. The question is whether you can sustain it.",
    },
    { type: "h2", text: "Which to choose" },
    {
      type: "ul",
      items: [
        "**Same voice on both, low effort wanted** — bridge them.",
        "**Different emphasis per network, or you want to plan ahead** — schedule to both.",
        "**Small volume, high care** — post natively and do not automate it.",
      ],
    },
    { type: "h2", text: "One thing everyone gets wrong" },
    {
      type: "p",
      text: "Cross-posting is not a distribution strategy on its own. The networks have different cultures — [they differ more than they look](/blog/bluesky-vs-mastodon-vs-threads) — and a stream of posts that obviously came from somewhere else reads exactly like that. The people who do this well cross-post the durable things and write natively for the conversational ones.",
    },
    {
      type: "quote",
      text: "Automate the posts that would be the same anywhere. Write the rest where they belong.",
    },
    { type: "h2", text: "The mechanical differences that trip people up" },
    {
      type: "p",
      text: "Whichever route you take, four things do not translate cleanly between the two networks, and knowing them in advance saves a lot of confusion:",
    },
    {
      type: "ul",
      items: [
        "**Content warnings have no Bluesky equivalent.** A CW either gets dropped or folded into the post text. Neither is what you meant.",
        "**Editing only exists on Mastodon.** Anything bridged or duplicated to Bluesky is frozen once posted.",
        "**Mentions do not resolve across networks.** Mentioning a Mastodon user in a post that lands on Bluesky produces text, not a link to anyone.",
        "**Alt text should travel, and sometimes does not.** Check it once with a real image rather than assuming.",
      ],
    },
    { type: "h2", text: "Replies are the part people forget" },
    {
      type: "p",
      text: "Getting posts onto both networks is the easy half. The half that determines whether being on both is worth it is whether you see and answer the replies.",
    },
    {
      type: "p",
      text: "A bridge does carry replies back, which is its real advantage over scheduling to both — with a scheduler you have two sets of conversations in two apps, and one of them will get neglected. Be honest about whether you will actually check both before adding a second network.",
    },
  ],
};

/** #8 — ties to a real product strength and a query with weak coverage. */
const BLUESKY_THREADS: BlogPost = {
  slug: "bluesky-threads-guide",
  title: "Bluesky threads: how to write one people actually read",
  excerpt:
    "How threading works on Bluesky, why auto-splitting a long post produces a bad thread, and how to structure one that holds attention.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt:
    "Five connected post cards forming a visual thread toward a paper airplane",
  hue: 205,
  body: [
    {
      type: "p",
      text: "A Bluesky thread is a chain of posts, each a reply to the one before. There is no separate thread feature and no special formatting — it is replies, all the way down. Which is worth understanding, because it explains most of what makes threads work or fail here.",
    },
    { type: "h2", text: "The 300-character constraint is doing you a favour" },
    {
      type: "p",
      text: "Each post in the chain gets 300 characters, counted as grapheme clusters. That is tight, and it is the reason Bluesky threads tend to read better than the equivalent elsewhere: you cannot ramble inside a segment. Every one has to earn its place.",
    },
    {
      type: "p",
      text: "The failure mode is treating the limit as an obstacle rather than a structure. A thread that is one long thought chopped into 300-character pieces reads like exactly that.",
    },
    { type: "h2", text: "Why auto-splitting produces a worse thread" },
    {
      type: "p",
      text: "Some scheduling tools take a long block of text and split it automatically at the character limit. It is convenient, and it is the wrong shape.",
    },
    {
      type: "p",
      text: "An automatic split has no idea where your argument turns. It breaks mid-sentence, strands a clause at the top of the next post, and gives you no way to attach an image to segment three — because it does not know segment three exists until it counts to 300 for the third time.",
    },
    {
      type: "quote",
      text: "A thread is a sequence of points, not a paragraph with breaks in it. The tooling should let you say where the points are.",
    },
    {
      type: "p",
      text: "Writing each segment yourself — whether in the Bluesky app or in a tool that supports explicit segments, as [PostDaily does](/bluesky-scheduler) — costs a couple of minutes and changes how the thread reads.",
    },
    { type: "h2", text: "Structure that holds attention" },
    {
      type: "ul",
      items: [
        "**The first post has to work alone.** It is the only one most people see, because it is the only one in the feed. If it needs post two to make sense, it will not travel.",
        "**One idea per post.** If a segment contains two thoughts, it should be two segments.",
        "**Do not number them.** Numbering promises length, and length is not a selling point. It also breaks badly if you add a post later.",
        "**Put media where it lands.** An image in post four, next to the point it illustrates, is worth more than four images in post one.",
        "**End deliberately.** A thread that stops because you ran out of things to say feels different from one that stops because it arrived somewhere.",
      ],
    },
    { type: "h2", text: "The chronological feed changes the maths" },
    {
      type: "p",
      text: "Bluesky's default Following feed is chronological. Nothing resurfaces a thread later because it started performing — if people were not looking, it did not happen.",
    },
    {
      type: "p",
      text: "Practically that means threads live or die on the first post and on when you post them, far more than on total length. It is also why posting a thread and then reposting the top of it hours later is a normal and reasonable thing to do here, rather than the faux pas it would be on an algorithmic network.",
    },
    { type: "h2", text: "Scheduling one" },
    {
      type: "p",
      text: "Bluesky has no built-in scheduler, so a scheduled thread means a tool. The one thing to verify: the whole chain has to publish in order, with each segment's media attached to the right post, and it needs to tell you if segment four fails after segments one to three went out. A half-published thread is worse than none.",
    },
    { type: "h2", text: "Replying to your own thread later" },
    {
      type: "p",
      text: "Because a thread is just replies, you can add to one days later and it slots in exactly as if it had always been there. That is genuinely useful and underused — an update, a correction, a follow-up result all belong on the original thread rather than in a new post nobody connects to it.",
    },
    {
      type: "p",
      text: "It also means a correction is a reply, not an edit. Bluesky has no editing, so a thread with a mistake in post two is fixed by replying with the correction, not by rewriting history.",
    },
    { type: "h2", text: "When not to write a thread" },
    {
      type: "ul",
      items: [
        "**When it fits in one post.** A single 300-character post that lands is worth more than three that dilute it.",
        "**When it is really an article.** Past roughly eight segments you are writing a blog post in a bad format. Write the post and link it.",
        "**When the payoff is at the end.** Threads are read top-down and abandoned steadily; burying the point in post seven means most people never reach it.",
      ],
    },
    {
      type: "p",
      text: "The full setup is in [the Bluesky scheduling guide](/blog/how-to-schedule-bluesky-posts).",
    },
  ],
};

/** #9 — cluster anchor. Not commercial; its job is to be linkable. */
const WHAT_IS_THE_FEDIVERSE: BlogPost = {
  slug: "what-is-the-fediverse",
  title: "What is the fediverse? A plain guide for people who post",
  excerpt:
    "Servers, ActivityPub, and why Mastodon works the way it does — explained for someone who wants to post, not build a server.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "8 min read",
  imageAlt:
    "Independent colorful community servers connected in a shared network",
  hue: 160,
  body: [
    {
      type: "p",
      text: "The fediverse is a group of social networks that can talk to each other. Not one site with one company behind it, but thousands of independently run servers that pass messages back and forth using a shared standard.",
    },
    {
      type: "p",
      text: "That is the whole idea. Everything else — the server names in handles, the culture, the reason it feels different — follows from it.",
    },
    { type: "h2", text: "The email comparison, and where it breaks" },
    {
      type: "p",
      text: "The usual explanation is email: your address is on one provider, mine is on another, and we can still write to each other because everyone agreed on a protocol. That is exactly right, and it is the fastest way to understand handles like @you@yourserver.social.",
    },
    {
      type: "p",
      text: "Where the comparison misleads is culture. Email providers are interchangeable utilities. Fediverse servers are not — they have visible admins, stated rules, distinct communities, and moderation policies that differ enormously. Which server you join genuinely shapes your experience in a way your email provider does not.",
    },
    { type: "h2", text: "ActivityPub is the agreement" },
    {
      type: "p",
      text: "The protocol underneath is **ActivityPub**, a W3C standard that became a recommendation in 2018. It defines how servers tell each other that someone posted, followed, liked or replied.",
    },
    {
      type: "p",
      text: "Because it is a standard rather than a product, different kinds of software can implement it and interoperate. Mastodon is microblogging. PeerTube is video. Pixelfed is photos. Someone on Mastodon can follow a PeerTube channel, because both speak the same language.",
    },
    { type: "h2", text: "What this means when you post" },
    {
      type: "ul",
      items: [
        "**Your server holds your account.** Your posts live there, your admin runs it, and its rules apply to you.",
        "**Your reach is not your server.** People on other servers follow you normally. Being on a small server does not mean a small audience.",
        "**Feeds are chronological by default.** There is no engagement algorithm deciding what surfaces. Timing matters more, and outrage travels less.",
        "**Moderation is local and opinionated.** Servers block other servers. That is a feature, and it is why choosing one with a moderation policy you agree with matters.",
        "**You can move.** Migration carries your followers to a new server. It does not carry your posts, and it does not carry anything you had scheduled.",
      ],
    },
    { type: "h2", text: "How Bluesky differs" },
    {
      type: "p",
      text: "Bluesky is decentralised too, but on a different protocol — the AT Protocol — so it is not part of the fediverse and the two cannot talk directly without a bridge.",
    },
    {
      type: "p",
      text: "The architectures differ in a way you can feel. ActivityPub pushes: your server delivers your post to the servers of people who follow you. AT Protocol pulls: services query a relay for events. In practice AT Protocol makes stronger portability guarantees and offers algorithmic choice, while ActivityPub gives your server's admin more say and defaults to chronological.",
    },
    {
      type: "p",
      text: "Neither is simply better. They are different bets about where control should sit. If you want to be on both, [there are three ways to do it](/blog/cross-post-bluesky-mastodon) — and [a fuller comparison of the three networks](/blog/bluesky-vs-mastodon-vs-threads) if you are still choosing.",
    },
    { type: "h2", text: "Where Threads fits" },
    {
      type: "p",
      text: "Meta's Threads has been connecting to the fediverse over ActivityPub. Fediverse users can follow Threads accounts that have opted in, and replies from Mastodon can appear back on Threads.",
    },
    {
      type: "p",
      text: "It is partial and it is deliberately walled off — fediverse content sits outside the main ranked feed rather than mixed into it. The asymmetry is also real: Meta operates at a scale that dwarfs the rest of the network, which is why the integration is welcomed by some fediverse admins and blocked outright by others.",
    },
    { type: "h2", text: "Picking a server" },
    {
      type: "p",
      text: "The advice people give is to pick a server matching your interests. Reasonable, and slightly overstated — you can follow anyone from anywhere, so the interest match matters less than people suggest.",
    },
    {
      type: "p",
      text: "What actually matters: is it run by someone who seems likely to still be running it in two years, does it have a moderation policy you agree with, and is it big enough to be stable without being so big it is impersonal. If in doubt, join a general-purpose one. Moving later is a supported operation, not a disaster.",
    },
    {
      type: "p",
      text: "Once you are posting regularly, [scheduling works differently here too](/blog/how-to-schedule-mastodon-posts) — Mastodon has the feature, it is just not where you would look.",
    },
  ],
};

/** #10 — high-interest comparison. Only works if it is genuinely even-handed. */
const BLUESKY_VS_MASTODON_VS_THREADS: BlogPost = {
  slug: "bluesky-vs-mastodon-vs-threads",
  title: "Bluesky vs Mastodon vs Threads: which should you post on?",
  excerpt:
    "Bluesky, Mastodon and Threads look similar and behave nothing alike. What each network is actually good at, and who should pick which one.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "9 min read",
  imageAlt: "One post branching into three distinct social network communities",
  hue: 320,
  body: [
    {
      type: "p",
      text: "These three get grouped together because they are all text-first and all arrived in the same window. Underneath they are three genuinely different bets about how a social network should work, and the differences show up in your day-to-day posting rather than in the technical detail.",
    },
    {
      type: "p",
      text: "I build a scheduling tool that publishes to all three, so I have no reason to prefer one. What follows is the honest read.",
    },
    { type: "h2", text: "The short version" },
    {
      type: "ul",
      items: [
        "**Bluesky** — chronological by default, algorithmic if you choose. Best if you want reach without an algorithm deciding for you.",
        "**Mastodon** — chronological, community-moderated, no algorithm at all. Best if you want conversation over reach.",
        "**Threads** — algorithmic, enormous, tied to Instagram. Best if you already have an Instagram audience.",
      ],
    },
    { type: "h2", text: "How posts actually reach people" },
    {
      type: "p",
      text: "This is the difference that matters most and the one least often explained.",
    },
    {
      type: "p",
      text: "**Threads** ranks. An algorithm decides who sees what, which means a good post can find an audience far beyond your followers — and a post at a bad moment can be resurfaced later rather than lost. Volume and consistency are rewarded.",
    },
    {
      type: "p",
      text: "**Mastodon** does not rank at all. Your followers see your posts in order, and that is the whole mechanism. Nothing goes viral by algorithm; things travel by people boosting them. Reach is smaller and more deliberate.",
    },
    {
      type: "p",
      text: "**Bluesky** does both, and lets you choose. The default Following feed is chronological, but custom feeds are a first-class feature and anyone can build one. That makes discovery possible without making it compulsory.",
    },
    {
      type: "quote",
      text: "On Threads you are writing for an algorithm. On Mastodon you are writing for the people who chose to follow you. Bluesky lets you decide which.",
    },
    { type: "h2", text: "Culture, which matters more than features" },
    {
      type: "p",
      text: "**Mastodon** is the most opinionated. Content warnings are used liberally and expected. Self-promotion lands badly. Alt text on images is close to a social requirement rather than an accessibility nicety. If you arrive posting like a marketer, you will have a bad time, and that is by design.",
    },
    {
      type: "p",
      text: "**Bluesky** feels closer to early Twitter — faster, funnier, more tolerant of self-promotion, with a strong technical and creative population. Custom feeds mean niche communities form quickly.",
    },
    {
      type: "p",
      text: "**Threads** is the most mainstream and the most brand-friendly, with the least tolerance for anything that reads as argumentative. Reach is real; conversation is thinner.",
    },
    { type: "h2", text: "Practical constraints" },
    {
      type: "ul",
      items: [
        "**Post length** — Bluesky 300 characters, Mastodon 500 by default (servers vary), Threads 500.",
        "**Scheduling** — Bluesky has none built in. Mastodon has it in the API but not in the official apps. Threads supports scheduling through its API.",
        "**Editing** — Mastodon lets you edit. Bluesky does not.",
        "**Content warnings** — Mastodon only.",
        "**Portability** — Mastodon migration moves followers, not posts. Bluesky's protocol makes stronger portability guarantees. Threads is Meta's.",
      ],
    },
    { type: "h2", text: "Do they connect?" },
    {
      type: "p",
      text: "Mastodon and Threads both speak ActivityPub, so a Threads account that has opted in can be followed from Mastodon, with replies flowing back. It is partial: fediverse content sits outside the main ranked feed by design.",
    },
    {
      type: "p",
      text: "Bluesky is on a different protocol and cannot talk to either directly — though [a bridge exists](/blog/cross-post-bluesky-mastodon) if you want Bluesky and Mastodon connected.",
    },
    { type: "h2", text: "So which one" },
    {
      type: "p",
      text: "**Pick Threads** if you have an Instagram following. Bringing it across is the single biggest advantage available on any of these networks, and nothing else compensates for starting from zero.",
    },
    {
      type: "p",
      text: "**Pick Bluesky** if you want to build something new and want reach to be possible without being algorithmic. It is the best of the three for finding an audience from a standing start.",
    },
    {
      type: "p",
      text: "**Pick Mastodon** if you want conversation with people who chose you, and are genuinely uninterested in scale. It rewards a completely different behaviour from the other two, and people who go in expecting reach tend to leave disappointed — [more so if you are posting as an organisation](/blog/mastodon-for-project-accounts).",
    },
    {
      type: "p",
      text: "**Pick more than one** only if you can write for each. Syndicating identical posts to three networks is visible from a distance and works on none of them. If you do run several, [the cross-posting options are here](/blog/cross-post-bluesky-mastodon).",
    },
  ],
};

/** #11 — the motivated slice of a SERP we cannot win head-on. */
const FACEBOOK_WITHOUT_BUSINESS_SUITE: BlogPost = {
  slug: "schedule-facebook-posts-without-business-suite",
  title: "How to schedule Facebook Page posts without Meta Business Suite",
  excerpt:
    "Business Suite is free and does the job for Facebook and Instagram. Here is exactly when it stops being the right tool, and what to use instead.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt: "A page post moving directly through a calendar to a clock",
  hue: 230,
  body: [
    {
      type: "p",
      text: "Meta Business Suite schedules Facebook Page posts for free. If you post only to Facebook and Instagram, it is a perfectly good tool and this article should end here — you do not need to pay anyone.",
    },
    {
      type: "p",
      text: "People go looking for an alternative for fairly specific reasons, and it is worth being clear about which ones are real.",
    },
    { type: "h2", text: "Reasons that are real" },
    {
      type: "p",
      text: "**You post somewhere other than Facebook and Instagram.** This is the big one. Business Suite covers Meta's own networks and nothing else, so the moment Bluesky, Mastodon, LinkedIn or TikTok enter the picture you are running two systems and two calendars.",
    },
    {
      type: "p",
      text: "**You want one calendar across everything.** Related but distinct: even if the other networks are a small part of what you do, planning a month in two places is where consistency dies.",
    },
    {
      type: "p",
      text: "**Someone needs to approve posts.** Business Suite handles Page roles, not editorial approval. If a client or a colleague signs off on content before it goes out, that is an email thread with Business Suite.",
    },
    {
      type: "p",
      text: "**You schedule further ahead than it allows.** Meta limits how far in advance you can schedule, and reports of the exact limit vary by surface and change over time — check it against your own account rather than trusting a number in an article, including this one.",
    },
    { type: "h2", text: "Reasons that usually are not" },
    {
      type: "ul",
      items: [
        '**"It keeps failing."** Most Business Suite scheduling failures are a stale browser session, a Page permission that changed, or media in a format Facebook will not take. A different tool hits the same Graph API and will fail the same way.',
        '**"The interface is bad."** It genuinely is. That is not usually worth a subscription on its own.',
        '**"I want better analytics."** Meta gives you more Page data than most third-party tools can, because they own it. Third-party analytics are usually a subset presented more clearly.',
      ],
    },
    { type: "h2", text: "What Pages-only means, whatever you use" },
    {
      type: "p",
      text: "Facebook's API publishes to **Pages**, not personal profiles. No third-party scheduler can post to your personal timeline, whatever a pricing page implies. If you are posting as yourself, you need a Page first — and that constraint is Meta's, not the tool's. Groups have been the same since Meta removed the Groups API in April 2024; [what still works for Group posts](/blog/schedule-facebook-group-posts) is its own short guide, and [how to schedule Facebook posts](/blog/how-to-schedule-facebook-posts) covers what Meta's own tools can schedule and how far ahead.",
    },
    { type: "h2", text: "What to look for in an alternative" },
    {
      type: "ul",
      items: [
        "**Does it cover the other networks you actually use**, or just claim to?",
        "**What happens when a post fails?** Facebook connections expire. The useful question is whether you find out, and whether the posts queued behind the broken connection are held or burned through one at a time.",
        "**Does it handle Reels and Stories**, or only feed posts? They have different rules and a tool that flattens them will surprise you.",
        "**Can it post the first comment?** Links belong there on a Page post, and doing it manually sixty seconds after publishing is not a workflow.",
      ],
    },
    {
      type: "p",
      text: "[PostDaily does all four](/facebook-scheduler), which is disclosure rather than a recommendation — the list is worth applying to whatever you look at.",
    },
    {
      type: "h2",
      text: "Running both, which is what most people end up doing",
    },
    {
      type: "p",
      text: "The either-or framing is slightly false. A common and sensible setup is Business Suite for Facebook and Instagram, and something else for everything beyond Meta.",
    },
    {
      type: "p",
      text: "It works, and the cost is a real one: two calendars. You lose the single view of what is going out this week, which is the thing that keeps a posting habit alive. Worth doing if Meta is the overwhelming majority of your posting and a genuine hassle if it is not.",
    },
    {
      type: "p",
      text: "If you do run both, pick one as the source of truth for planning even if publishing happens in two places. Two half-maintained calendars is worse than either alone.",
    },
    { type: "h2", text: "Before you move anything" },
    {
      type: "ul",
      items: [
        "**Check your Page role.** Publishing through any tool depends on your access to the Page. If someone else created it and gave you a limited role, no tool can work around that.",
        "**Do not cancel Business Suite** — there is nothing to cancel. It is free, so run both during a transition and drop it when you stop opening it. The same overlap-then-drop approach works [moving off any scheduler](/blog/moving-from-buffer).",
        "**Move one Page first.** Confirm a real post publishes and looks right before moving anything else.",
        "**Know what happens on failure.** Facebook connections expire. Whether you find out, and what happens to the posts queued behind the broken one, is the difference that matters — see [why scheduled posts fail](/blog/why-scheduled-posts-fail), and for Facebook in particular, [every reason a scheduled post doesn't go out](/blog/facebook-scheduled-post-not-posting).",
      ],
    },
    { type: "h2", text: "The honest summary" },
    {
      type: "quote",
      text: "If Facebook and Instagram are your whole world, use Business Suite. The reason to leave is breadth, not quality.",
    },
  ],
};

/** #12 — broad and competitive; earns links via the template, not the prose. */
const CONTENT_CALENDAR: BlogPost = {
  slug: "social-media-content-calendar",
  title: "A social media content calendar that survives a real week",
  excerpt:
    "Most content calendars fail in week three. Here is a structure built around the weeks when you have no time, because those decide whether it works.",
  category: "Playbooks",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "8 min read",
  imageAlt:
    "A weekly content calendar with movable post cards and an open slot",
  hue: 30,
  body: [
    {
      type: "p",
      text: "Almost every content calendar works for two weeks. The interesting question is what happens in week three, when something urgent lands and the calendar is the first thing to go.",
    },
    {
      type: "p",
      text: "A calendar that only works when you have time is not a system, it is a mood. What follows is built the other way round — designed for the bad weeks, because those are the ones that decide whether you are still posting in six months.",
    },
    { type: "h2", text: "Why the usual advice fails" },
    {
      type: "p",
      text: "The standard version is a spreadsheet with a row per day and columns for network, copy, asset and status. It fails for a predictable reason: **it requires a decision every single day**, and a bad week removes exactly the capacity those decisions need.",
    },
    {
      type: "p",
      text: "The fix is not more discipline. It is moving the decisions out of the day they are needed.",
    },
    { type: "h2", text: "Decide the slots once, not the posts" },
    {
      type: "p",
      text: "Start with a weekly grid of times, not a list of posts. Three or four slots a week you can genuinely sustain — not the number you would like to hit.",
    },
    {
      type: "p",
      text: "Then the daily question stops being *what should I post today* and becomes *is there anything in the queue*. That is a much smaller question and it survives a bad week.",
    },
    {
      type: "quote",
      text: "Set the rhythm once. After that, the only recurring job is keeping the queue from running dry.",
    },
    { type: "h2", text: "Batch by type, not by day" },
    {
      type: "p",
      text: "Writing Monday's post, then Tuesday's, then Wednesday's means switching modes constantly. Writing four of the same kind of thing in one sitting is dramatically faster, because the hard part is getting into the mode, not the writing.",
    },
    {
      type: "p",
      text: "A workable split for most people is three or four recurring types — something you learned, something you made, something you are reading, something you have an opinion about. Fill an afternoon with one type at a time. If someone else signs the posts off, [batch the approvals too](/blog/client-approval-workflow-freelancers).",
    },
    { type: "h2", text: "Keep a buffer, and name the number" },
    {
      type: "p",
      text: "Decide how many posts ahead you want to be, and treat dropping below it as the actual trigger to write — rather than waiting until you are empty.",
    },
    {
      type: "p",
      text: "Two weeks is a reasonable target. It is long enough to absorb a bad week and short enough that the content is not stale by the time it goes out.",
    },
    { type: "h2", text: "Plan the month, schedule the fortnight" },
    {
      type: "ul",
      items: [
        "**Monthly, 30 minutes** — what is happening, what you want to say, roughly when. Themes, not posts.",
        "**Fortnightly, 90 minutes** — write and queue. This is the only block that has to happen.",
        "**Daily, 5 minutes** — check nothing failed, reply to anything that needs a person.",
      ],
    },
    {
      type: "p",
      text: "That is about two hours a month of real work. The reason most calendars ask for more is that they conflate planning with writing with publishing, and doing all three at once is what makes it feel enormous.",
    },
    {
      type: "p",
      text: "For the monthly pass, start from the dates that are fixed anyway. The [2027 content calendar template](/tools/content-calendar-2027) lists every one worth planning around, and downloads as a spreadsheet in bulk-upload columns, so the themes you pick there can be written and queued in one go.",
    },
    { type: "h2", text: "What to do when you miss anyway" },
    {
      type: "p",
      text: "You will. The failure mode is not missing a week — it is treating a missed week as evidence the system does not work, and abandoning it.",
    },
    {
      type: "p",
      text: "Skip the slot. Do not double up to catch up; nobody noticed the gap and two posts in a day is more visible than none. Refill at the next batching session and carry on.",
    },
    { type: "h2", text: "Where a tool helps, and where it does not" },
    {
      type: "p",
      text: "A scheduler removes the publishing step and the remembering step. It does not do the planning or the writing, and no tool will — anything promising to is selling you a content mill.",
    },
    {
      type: "p",
      text: "What is genuinely worth having: a queue that fills from your slots automatically, a calendar you can see a month on, and a system that tells you when something failed to publish rather than quietly stopping.",
    },
  ],
};

/** #13 */
const HOW_OFTEN_BLUESKY: BlogPost = {
  slug: "how-often-to-post-on-bluesky",
  title: "How often should you post on Bluesky?",
  excerpt:
    "There is no magic number, but there is a right way to think about it — and on a chronological feed the answer is different from everywhere else.",
  category: "Playbooks",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt: "Six evenly spaced blue post cards orbiting a calm central rhythm",
  hue: 190,
  body: [
    {
      type: "p",
      text: "Every answer you will find to this question is a number someone made up. I am not going to add one. What is genuinely useful is understanding why Bluesky's answer differs from the network you came from.",
    },
    { type: "h2", text: "The chronological feed changes everything" },
    {
      type: "p",
      text: "On an algorithmic network, posting more is a lottery ticket: each post is another chance the algorithm picks you up, and the ones that miss cost almost nothing because most followers never saw them anyway.",
    },
    {
      type: "p",
      text: "On Bluesky's default Following feed, every post you make is shown to your followers in order. Posting five times in an hour does not give you five chances — it gives your followers five posts in a row from you, which is a different and less welcome experience.",
    },
    {
      type: "quote",
      text: "On an algorithmic feed, volume is a bet. On a chronological one, volume is something you are doing to people.",
    },
    { type: "h2", text: "What that means practically" },
    {
      type: "ul",
      items: [
        "**Spacing matters more than count.** Four posts spread across a day reads completely differently from four in twenty minutes.",
        "**Threads count as one.** A ten-post thread occupies one slot in the feed, not ten. It is the efficient shape for a long thought.",
        "**Replies are nearly free.** They do not land in your followers' main feed the way posts do, so conversation has a much lower cost than broadcasting.",
        "**Missing a day costs less than here than elsewhere.** There is no algorithm penalising inconsistency. The cost of a gap is that people forget you, which is slower and more forgiving.",
      ],
    },
    { type: "h2", text: "A reasonable starting point" },
    {
      type: "p",
      text: "One to three posts a day, spaced out, plus replies. Not because that number is optimal — because it is sustainable, and sustainability is the variable that actually determines outcomes over six months.",
    },
    {
      type: "p",
      text: "Then adjust based on what you see rather than what an article said. If engagement per post drops as you post more, you are past your number. If people are consistently missing your posts, you are under it.",
    },
    { type: "h2", text: "The thing worth optimising instead" },
    {
      type: "p",
      text: "Timing. On a chronological feed, when you post determines who sees it far more than how often you post. Two posts landing when your followers are reading beats six spread across dead hours.",
    },
    {
      type: "p",
      text: "Which is the whole argument for a queue rather than posting whenever you happen to open the app — [set the slots once](/bluesky-scheduler) and the timing question stops recurring. Though on the evidence, [which slots you pick matters less than you would think](/blog/best-time-to-post-on-bluesky).",
    },
    { type: "h2", text: "The rate that actually kills accounts" },
    {
      type: "p",
      text: "It is not too many posts. It is stopping.",
    },
    {
      type: "p",
      text: "Almost everyone who abandons a network does it the same way: an ambitious rate for a fortnight, a busy week, a gap, and then the gap becomes the state. The number that matters is the one you can hold in the week when you have no time — which is almost always lower than the number you would pick on a good day.",
    },
    {
      type: "quote",
      text: "Pick the rate you can sustain in your worst week, not your best one. Everything above that is a bonus rather than a commitment.",
    },
    { type: "h2", text: "Replies are the underrated half" },
    {
      type: "p",
      text: "Posting is the visible activity, so it gets all the attention in advice like this. On Bluesky, replies do more for reach than posts do — they put you in front of someone else's audience rather than your own, and they cost nothing in your followers' feeds.",
    },
    {
      type: "p",
      text: "An account posting once a day and replying often will almost always grow faster than one posting four times a day into silence. If you are choosing where to spend limited attention, spend it there — or on [making the posts themselves better](/blog/bluesky-threads-guide).",
    },
    { type: "h2", text: "How to tell you have it wrong" },
    {
      type: "ul",
      items: [
        "**Engagement per post falling as you post more** — you are above your number.",
        "**People replying to things days late** — they are catching up rather than reading live, which usually means your timing is off rather than your volume.",
        "**You are posting to hit a target** rather than because you had something to say — the point at which the number is running you.",
        "**You dread it** — the most reliable signal, and the one people ignore longest.",
      ],
    },
  ],
};

/** #14 */
const MASTODON_PROJECT_ACCOUNT: BlogPost = {
  slug: "mastodon-for-project-accounts",
  title: "Running a Mastodon account for a project or community",
  excerpt:
    "Posting as an organisation on a network built for people. What works, what gets you muted, and how to handle it when more than one person posts.",
  category: "Playbooks",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "7 min read",
  imageAlt: "A small team organizing shared posts and a community network",
  hue: 145,
  body: [
    {
      type: "p",
      text: "Mastodon is built around individual people, and it shows. An account representing a project, a community or a company is a slightly awkward fit — not unwelcome, but subject to expectations that are rarely written down anywhere.",
    },
    {
      type: "p",
      text: "Here is what those expectations actually are, from watching plenty of project accounts do well and plenty do badly.",
    },
    { type: "h2", text: "Where to host it" },
    {
      type: "p",
      text: "Three options, in descending order of effort: run your own single-user server, join a server for your field, or join a general-purpose one.",
    },
    {
      type: "p",
      text: "Running your own gives you a handle at your own domain, which reads well and is genuinely more durable. It also makes you a server admin, with the moderation and maintenance that implies. Worth it if the account matters; overkill if you post monthly.",
    },
    {
      type: "p",
      text: "Joining a topical server puts you among the people you want to reach — but check its rules first. Some explicitly restrict organisational accounts, and finding that out after you have built followers is expensive.",
    },
    { type: "h2", text: "What gets a project account muted" },
    {
      type: "ul",
      items: [
        "**Broadcasting only.** An account that posts and never replies reads as a feed, not a participant. Replies are where the goodwill is.",
        '**Announcement voice.** "We are thrilled to announce" is the register of a press release, and it lands badly here.',
        "**No alt text.** On Mastodon this is close to a social requirement rather than a nicety, and a project account without it looks careless.",
        "**Ignoring content warnings.** If you post something people would reasonably want to opt out of, use one. The convention is stronger than elsewhere.",
        "**Cross-posting visibly.** A stream that obviously originated on another network reads exactly like that. Syndicate the durable things; write the conversational ones here.",
      ],
    },
    { type: "h2", text: "When more than one person posts" },
    {
      type: "p",
      text: "Mastodon has no built-in concept of a team account — one login, one account. Which leaves you sharing a password, or using a tool with real seats.",
    },
    {
      type: "p",
      text: "A convention worth adopting either way: sign posts with initials when a specific person is speaking. It is a small thing that makes an organisational account feel like the people behind it, which is the whole game on this network.",
    },
    { type: "h2", text: "A rhythm that works" },
    {
      type: "p",
      text: "Most successful project accounts settle somewhere around: a few substantive posts a week, replies whenever someone engages, and boosts of things the community made. The ratio matters more than the volume — an account that boosts and replies more than it broadcasts is read as a participant. [Planning that rhythm](/blog/social-media-content-calendar) is most of what keeps it going.",
    },
    { type: "h2", text: "Alt text is not optional here" },
    {
      type: "p",
      text: "Worth its own section because it is the single most common way a project account signals it has not read the room. On Mastodon, describing your images is a normal expectation rather than an accessibility extra, and accounts that skip it get told — politely the first time.",
    },
    {
      type: "p",
      text: "It is also genuinely easy to systematise: write the alt text when you write the post, not at publishing time. Any tool worth using will let you attach it as part of the draft.",
    },
    { type: "h2", text: "What to do about the algorithm you do not have" },
    {
      type: "p",
      text: "Coming from a ranked network, the instinct is to post more and hope something catches. Nothing catches here — there is no mechanism for it. What travels is boosts, and boosts come from people, which means the growth loop is entirely social.",
    },
    {
      type: "p",
      text: "In practice that means the highest-leverage thing a project account does is not posting at all. It is replying to people in your field, boosting their work, and being visibly a participant. The posts land better afterwards because people know who you are.",
    },
    {
      type: "quote",
      text: "On a network with no algorithm, reach is a social relationship rather than a distribution channel.",
    },
    { type: "h2", text: "Handing it over" },
    {
      type: "p",
      text: "Project accounts outlive the person who set them up more often than personal ones do, so decide early where the credentials live and who has them. A shared password manager entry, not a Slack message.",
    },
    {
      type: "p",
      text: "If you run your own single-user server, this includes the server itself — which is the strongest argument for joining an existing one unless someone is genuinely committed to maintaining it.",
    },
    {
      type: "p",
      text: "Scheduling handles the substantive posts. It cannot handle the replies, and an account that only does the scheduled half is the one people mute. If you are setting that up, [the practical guide is here](/blog/how-to-schedule-mastodon-posts).",
    },
  ],
};

/** #15 */
const WHY_SCHEDULED_POSTS_FAIL: BlogPost = {
  slug: "why-scheduled-posts-fail",
  title: "Why scheduled posts fail, and what to do about each cause",
  excerpt:
    "Expired connections, rejected media, rate limits, silent tools. The handful of things that actually go wrong, and how to tell them apart.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "7 min read",
  imageAlt:
    "A publishing conveyor pausing one post at a repair and retry station",
  hue: 20,
  body: [
    {
      type: "p",
      text: "A scheduled post that does not go out is a specific kind of annoying, because you usually find out late and often by accident. The causes are fewer than you would think, and they are distinguishable if you know what to look for.",
    },
    { type: "h2", text: "The connection expired" },
    {
      type: "p",
      text: "By far the most common cause. Every network expires the access it grants a tool. Change your password, revoke an app, lose admin rights on a Page, or simply wait long enough, and the connection stops working.",
    },
    {
      type: "p",
      text: "**How to tell:** everything for one channel fails while others are fine. **The fix:** reconnect. **The thing to check afterwards:** whether the posts queued behind it were held or burned through one at a time at their scheduled slots. A tool that parks them lets you carry on; one that fails them individually leaves you with a column of failures and an empty queue.",
    },
    { type: "h2", text: "The media was rejected" },
    {
      type: "p",
      text: "Every network has its own rules about formats, dimensions, duration and file size, and they are not the same. A video that is fine on one is refused by another.",
    },
    {
      type: "p",
      text: "**How to tell:** posts with media fail, text-only posts succeed. **The fix:** usually re-encoding to H.264 MP4, which is the safest thing everywhere. Some tools convert automatically, which is worth knowing before you upload forty clips.",
    },
    { type: "h2", text: "You hit a limit you did not know about" },
    {
      type: "p",
      text: "Networks cap what an app can do in a window, and some caps are surprisingly low. Instagram counts each story frame against a daily quota. Bluesky limits video uploads per day. These are rarely documented where you would look.",
    },
    {
      type: "p",
      text: "**How to tell:** it worked earlier and stopped, without anything changing. **The fix:** wait, and spread posting out rather than batching everything into one hour.",
    },
    { type: "h2", text: "The post was too long for that network" },
    {
      type: "p",
      text: "Especially when posting the same text to several networks with different limits — and different counting rules. A post that fits Mastodon's 500 does not fit Bluesky's 300, and a tool that counts to one number is wrong on the other. [The per-network breakdown is here](/help/character-limits-by-network).",
    },
    { type: "h2", text: "Something changed on the network's side" },
    {
      type: "p",
      text: "Permissions get deprecated, endpoints change, a required field appears. Rare, affects everyone at once, and outside your control. **How to tell:** it is failing for other people too — check the tool's status page or their social account before debugging your own setup. Meta is the most frequent source of this, and [worth reading about separately](/blog/schedule-facebook-posts-without-business-suite).",
    },
    { type: "h2", text: "The failure mode that matters most" },
    {
      type: "quote",
      text: "The problem is rarely that a post failed. It is that you found out six days later.",
    },
    {
      type: "p",
      text: "Everything above is recoverable in minutes if you know about it. The thing worth checking when evaluating any scheduler is what happens on failure: does it retry transient errors, does it show you the network's actual error message rather than a generic one, and does it tell you unprompted?",
    },
    { type: "h2", text: "A five-minute diagnostic" },
    {
      type: "p",
      text: "When something fails and you do not know why, this order narrows it fast:",
    },
    {
      type: "ul",
      items: [
        "**Is it one channel or all of them?** One channel means the connection. All of them means the tool or the network.",
        "**Is it posts with media, or all posts?** Media-only means format or size.",
        "**Did it work earlier today?** Yes means you probably hit a limit. No means something changed.",
        '**Does the error name the network?** A message from Facebook or Bluesky is actionable. A generic "failed to publish" means your tool is not showing you what it was told, which is its own problem.',
        "**Can you publish the same post manually?** If yes, the network is fine and the problem is between you and the tool.",
      ],
    },
    { type: "h2", text: "Preventing most of it" },
    {
      type: "ul",
      items: [
        "**Reconnect channels before they break**, roughly every few months, rather than waiting for the failure.",
        "**Keep media in H.264 MP4 and standard image formats.** Exotic codecs are the second most common cause and the easiest to eliminate.",
        "**Spread posting out.** Batching forty posts into one hour is how you find rate limits.",
        "**Check the queue after any password change** on a connected account. That single action invalidates connections on several networks at once.",
      ],
    },
    { type: "h2", text: "The test worth doing once" },
    {
      type: "p",
      text: "Test it deliberately. Revoke a connection on purpose and see what your tool does — whether it tells you, what it does with the posts queued behind it, and whether reconnecting restores them or you have to rebuild the queue by hand.",
    },
    {
      type: "p",
      text: "It is five minutes and it tells you more than any review will. Everything else on this page is recoverable; a tool that fails silently is the one that costs you a month before you notice.",
    },
  ],
};

/** #16 */
const FREELANCER_APPROVALS: BlogPost = {
  slug: "client-approval-workflow-freelancers",
  title: "A social media approval workflow for freelancers with one client",
  excerpt:
    "Agency approval tooling is built for teams of twelve. Here is a workflow that fits one person and one client without adding seats or software.",
  category: "Playbooks",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt:
    "A post moving from creation through client approval into a scheduled tray",
  hue: 265,
  body: [
    {
      type: "p",
      text: "If you post on behalf of someone else, they want to see it first. Every tool that solves this properly is built for agencies — approval hierarchies, roles, seats per reviewer — and priced accordingly.",
    },
    {
      type: "p",
      text: "For one person and one client, that is enormous overkill, and the usual fallback is a spreadsheet and an email thread. Here is a middle path.",
    },
    { type: "h2", text: "Why the email thread fails" },
    {
      type: "ul",
      items: [
        "**No version of record.** Which draft did they approve — the one in the email, or the one after their reply?",
        "**No preview.** Copy in an email does not show what the post will actually look like, so you get approval on something and ship something else.",
        "**Approval by silence.** No reply becomes tacit yes, until the one time it does not.",
        "**Re-keying.** You paste approved copy into the scheduler by hand, which is where typos are introduced after review.",
      ],
    },
    { type: "h2", text: "What good looks like at this size" },
    {
      type: "p",
      text: "Three properties. Everything else is agency machinery you do not need:",
    },
    {
      type: "ul",
      items: [
        "**They see the real post**, rendered as it will appear, not copy in a document.",
        "**Approval is a recorded action**, not an email you have to keep.",
        "**Approved content publishes without re-keying**, so what shipped is what they saw.",
      ],
    },
    { type: "h2", text: "The seat problem" },
    {
      type: "p",
      text: "The thing that makes this expensive is that most tools model a reviewer as a user. Your client needs an account, an invitation, a password, and a seat on your plan — for something they do twice a month.",
    },
    {
      type: "p",
      text: "In practice clients do not complete that setup. They reply to the email instead, and you are back where you started.",
    },
    {
      type: "quote",
      text: "Any approval step your client has to create an account for is an approval step that will not happen.",
    },
    {
      type: "p",
      text: "A review link they open in a browser — no account, no seat — is the difference between a workflow that runs and one that exists on paper. It is why [PostDaily does approvals that way](/facebook-scheduler), which is disclosure rather than a pitch; the principle holds whatever you use.",
    },
    { type: "h2", text: "A workflow that fits" },
    {
      type: "ul",
      items: [
        "**Batch a fortnight** of posts in one sitting rather than sending them one at a time.",
        "**Send one link** covering all of them. One review session beats fifteen notifications.",
        "**Set a deadline in the message** — approve by Thursday or it goes as drafted. Removes the silence problem without nagging.",
        '**Keep rejections specific.** "Change the second line" beats "not quite right", and it is worth asking for that explicitly at the start of a relationship.',
      ],
    },
    { type: "h2", text: "Agreeing the rules before the first batch" },
    {
      type: "p",
      text: "Most approval friction is not tooling, it is an unstated expectation discovered late. Five minutes at the start of the relationship prevents most of it:",
    },
    {
      type: "ul",
      items: [
        "**Who has final say.** One person. If two people review, they will disagree in front of you and you will be the one resolving it.",
        "**What happens on silence.** State the default explicitly — approved, or held. Either is fine; ambiguity is not.",
        "**How many revision rounds** are included. Without a number, some clients will iterate indefinitely because nothing stops them.",
        "**What does not need approval.** Replies, usually. If every reply needs sign-off you do not have a social account, you have a press office.",
      ],
    },
    { type: "h2", text: "When approval is the wrong tool" },
    {
      type: "p",
      text: "Approval works for planned content. It does not work for anything time-sensitive, and trying to force it there is how accounts go silent during the exact moments they should be talking.",
    },
    {
      type: "p",
      text: "The usual resolution is a standing agreement: scheduled content goes through review, and anything reactive is either pre-authorised within agreed boundaries or simply does not happen. Deciding that in advance is much easier than deciding it at 4pm on a Friday.",
    },
    { type: "h2", text: "Keeping the record" },
    {
      type: "p",
      text: "The value of a recorded approval is almost entirely in the one conversation a year where someone asks why a post went out. A timestamped record of who approved what, attached to the post itself, ends that conversation in ten seconds.",
    },
    {
      type: "p",
      text: "Two rounds a month, fifteen minutes each. That is achievable, and it is roughly the ceiling of what a one-client relationship will bear before the process costs more than it protects. The same rhythm works for [an account run on behalf of a project](/blog/mastodon-for-project-accounts), where the reviewer is a colleague rather than a client.",
    },
  ],
};

/** #17 */
const BLUESKY_ANALYTICS: BlogPost = {
  slug: "bluesky-analytics-what-you-can-measure",
  title: "Bluesky analytics: what you can actually measure",
  excerpt:
    "Bluesky has no analytics dashboard, and the API exposes less than you would expect. An honest account of what is measurable and what is not.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt:
    "A magnifying glass examining post cards and simple performance charts",
  hue: 175,
  body: [
    {
      type: "p",
      text: "Bluesky does not have an analytics dashboard. There is no equivalent of Instagram Insights or Page analytics, and third-party tools cannot invent data the network does not publish.",
    },
    {
      type: "p",
      text: "That constraint is not a gap waiting to be filled by a clever tool — it is a design consequence. Worth understanding before you pay anyone for Bluesky analytics.",
    },
    { type: "h2", text: "What is measurable" },
    {
      type: "ul",
      items: [
        "**Likes, reposts and replies** per post. Public, countable, reliable.",
        "**Follower count** over time, if something records it — Bluesky itself does not keep the history.",
        "**Your own posting cadence**, which sounds trivial and is the number most people are wrong about.",
        "**Which posts travelled**, by comparing engagement across your own posts.",
      ],
    },
    { type: "h2", text: "What is not" },
    {
      type: "p",
      text: "**Impressions and reach.** This is the big one. Bluesky does not publish how many people saw a post, so nothing can report it. Any tool showing you Bluesky reach is estimating, and should say so.",
    },
    {
      type: "p",
      text: "**Follower demographics.** No age, location or interest data. It is not collected in a form anyone can query.",
    },
    {
      type: "p",
      text: "**Link clicks**, unless you measure them yourself with a redirect or UTM parameters at the other end.",
    },
    {
      type: "p",
      text: "**Profile views.** Not exposed.",
    },
    { type: "h2", text: "Why the chronological feed makes this less painful" },
    {
      type: "p",
      text: "On an algorithmic network, impressions matter enormously because the algorithm decides your reach and you are trying to reverse-engineer it. On a chronological feed, your reach is approximately your follower count — the feed does not choose.",
    },
    {
      type: "quote",
      text: "You are not missing a number that would change your decisions. On a chronological feed, engagement per post and posting time are most of the signal.",
    },
    { type: "h2", text: "What to actually track" },
    {
      type: "ul",
      items: [
        "**Engagement per post**, not totals. Totals go up because you posted more, which tells you nothing.",
        "**Time of day against engagement.** The highest-value thing you can learn on a chronological network, and it needs several weeks of varied posting to see.",
        "**Replies as a share of engagement.** A high ratio means conversation rather than passive scrolling, and it predicts follower growth better than likes.",
        "**Follower growth per week**, recorded by something, because Bluesky will not remember it for you.",
      ],
    },
    {
      type: "p",
      text: "None of that requires a paid tool. It requires something recording the numbers over time, because the network does not.",
    },
    { type: "h2", text: "How to record it without paying for anything" },
    {
      type: "p",
      text: "The whole problem is persistence, not access. Engagement counts are public on every post, so a spreadsheet updated weekly will get you most of the way: date, post, likes, reposts, replies, and your follower count that day.",
    },
    {
      type: "p",
      text: "That sounds primitive and it is genuinely enough for the first few months. The reason people move to a tool is not better numbers — it is that manually recording them survives about three weeks.",
    },
    { type: "h2", text: "Reading the numbers without fooling yourself" },
    {
      type: "p",
      text: "Two traps are worth naming, because both are easy and both lead to worse posting.",
    },
    {
      type: "p",
      text: "**Small samples look like signal.** One post doing four times your usual numbers is far more likely to be one influential person reposting it than a repeatable insight about what your audience wants. Look at a month, not a post.",
    },
    {
      type: "p",
      text: "**Follower count is a lagging, noisy number.** It moves for reasons unconnected to what you posted — someone added you to a starter pack, an account you follow mentioned you, a spam wave got purged. Treat a week-over-week trend as information and a single day as noise.",
    },
    { type: "h2", text: "What to do with what you learn" },
    {
      type: "ul",
      items: [
        "**If replies are low but likes are fine**, you are broadcasting. Ask more questions; post fewer conclusions.",
        "**If one time slot consistently outperforms**, move more posts into it rather than adding new slots — though [across 344 accounts we could not find a slot that reliably did](/blog/best-time-to-post-on-bluesky).",
        "**If engagement per post falls as volume rises**, you are past your sustainable rate — see [how often to post](/blog/how-often-to-post-on-bluesky).",
        "**If nothing correlates with anything**, you probably do not have enough posts yet. Two months is roughly the point where patterns become readable.",
      ],
    },
  ],
};

/** #18 */
const MOVING_FROM_BUFFER: BlogPost = {
  slug: "moving-from-buffer",
  title: "Moving from Buffer without losing your queue",
  excerpt:
    "Moving from Buffer: what transfers, what does not, and the order to switch in so you never have a day with nothing scheduled on any channel.",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "6 min read",
  imageAlt:
    "An orderly queue of posts crossing a bridge between two scheduling trays",
  hue: 250,
  body: [
    {
      type: "p",
      text: "Switching schedulers is mostly boring, with two parts that can go wrong: connections have to be re-authorised from scratch, and there is a window where you might have nothing queued anywhere.",
    },
    {
      type: "p",
      text: "Both are avoidable with ordering. Here is the sequence.",
    },
    { type: "h2", text: "What does not transfer" },
    {
      type: "p",
      text: "**Your connections.** Every network has to be re-authorised. This is an OAuth constraint, not a tool being awkward — access was granted to one application and cannot be handed to another. Budget ten minutes per network and expect to find at least one account whose password you have forgotten.",
    },
    {
      type: "p",
      text: "**Your history.** Published posts and their analytics stay where they are. Export anything you want to keep before you cancel, because access usually ends with the subscription.",
    },
    { type: "h2", text: "What can come across" },
    {
      type: "p",
      text: "**Scheduled content**, if you move it as data. Most tools export a queue to CSV and most import one, so a planned month is a spreadsheet rather than retyping.",
    },
    {
      type: "p",
      text: "The thing to check on the import side is whether it handles image URLs. If it does, media comes with the text. If it does not, you are re-uploading everything by hand, which is where the real cost of a migration hides.",
    },
    { type: "h2", text: "The order that avoids a gap" },
    {
      type: "ul",
      items: [
        "**Do not cancel first.** Overlap for one billing period. It costs one month and removes all the pressure.",
        "**Export the queue** from the old tool while you still have access.",
        "**Connect one channel** to the new tool and schedule one real post. Confirm it publishes and looks right before doing anything else.",
        "**Connect the rest**, then import the queue.",
        "**Run both for a fortnight**, with new posts going into the new tool only. The old queue drains naturally.",
        "**Cancel** once the old queue is empty and nothing has surprised you.",
      ],
    },
    { type: "h2", text: "Check before you commit, not after" },
    {
      type: "ul",
      items: [
        "**Every network you use is actually live**, not listed as coming soon.",
        "**Threads, first comments and per-network text** work the way you need, if you rely on them.",
        "**What happens when a post fails** — the question worth more than any feature comparison.",
      ],
    },
    { type: "h2", text: "Getting the CSV right" },
    {
      type: "p",
      text: "The export and the import rarely use the same column names, so there is usually a spreadsheet step in the middle. Three things are worth checking before you import a hundred rows and discover a systematic problem:",
    },
    {
      type: "ul",
      items: [
        "**Time zones.** Exports often carry UTC while you think in local time. A whole queue shifted by several hours is the classic migration failure, and it is only obvious once posts start going out at 3am.",
        "**Date format.** Day-first and month-first look identical for the first twelve days of a month and then diverge quietly.",
        "**One row per network.** If a post went to three channels, most exports give you three rows. Importing them as three separate posts is usually what you want, but not always.",
      ],
    },
    {
      type: "p",
      text: "Import ten rows first and look at what landed in the calendar. It takes two minutes and it catches all three of the above.",
    },
    { type: "h2", text: "The week after" },
    {
      type: "p",
      text: "Two things tend to surface once the migration is technically done.",
    },
    {
      type: "p",
      text: "**Notifications change.** Whatever told you a post failed before is now a different email from a different sender, and it will go to a folder you do not read. Find one deliberately — publish something that fails on purpose if necessary — so you know what the alert looks like.",
    },
    {
      type: "p",
      text: "**Your posting times may not have transferred.** Queue slots are a tool-specific concept and rarely survive a CSV. Rebuild the weekly grid before the queue drains, or posts will start landing at whatever the new tool's defaults are. If Meta is in the mix, [Business Suite is a separate decision](/blog/schedule-facebook-posts-without-business-suite).",
    },
    {
      type: "p",
      text: "And the honest note: if you run one to three channels, Buffer's per-channel pricing is genuinely cheap and switching may cost you more than it saves. [The full comparison is here](/alternatives/buffer), including the sizes where staying is the right call, and [what Buffer costs at 3, 10 and 25 channels](/blog/buffer-pricing) is worked out separately.",
    },
  ],
};

/**
 * The original-data study. This is the piece the whole content plan hangs on —
 * the only asset on the list people link to without being asked.
 *
 * Data: 35,546 public posts sampled from the Bluesky firehose (Jetstream),
 * narrowed to 8,373 English posts from 344 accounts holding 50–50,000
 * followers, each post at least 48 hours old. All public, unauthenticated
 * endpoints; no customer data of any kind.
 *
 * The finding reverses the genre, and the reason is worth preserving: pooled
 * across accounts the data shows a 2.9x spread between the best and worst hour,
 * which is roughly the number every competing article prints. Comparing each
 * account against ITSELF makes that spread disappear. The pooled version was
 * measuring which accounts post at which hours, not what time does.
 *
 * Re-run before updating any number here. Scripts and method are described in
 * the article itself so the claim is checkable.
 */
const BLUESKY_TIMING_STUDY: BlogPost = {
  slug: "best-time-to-post-on-bluesky",
  title: "We analysed 8,373 Bluesky posts. Posting time barely mattered.",
  excerpt:
    "Every guide gives you a best time to post. We sampled the firehose to check, and once you compare accounts against themselves, the effect nearly disappears.",
  category: "Playbooks",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "10 min read",
  imageAlt:
    "A clock and calendar surrounded by many post cards and a nearly level chart",
  hue: 200,
  body: [
    {
      type: "p",
      text: "Search for the best time to post on Bluesky and you will get a specific answer. Saturday at 5pm. Weekday evenings. Tuesday through Thursday, 9am to 3pm. The answers do not agree with each other, which is the first clue.",
    },
    {
      type: "p",
      text: "We sampled Bluesky's public firehose to check. The short version: **once you compare an account against itself rather than against other accounts, we could not detect a meaningful effect of posting time at all.**",
    },
    {
      type: "p",
      text: "That is not the answer we expected, and it is not a convenient answer for a company that sells scheduling software. The method is below so you can disagree with it.",
    },
    { type: "h2", text: "What we looked at" },
    {
      type: "ul",
      items: [
        "**35,546 public posts** collected from the Bluesky firehose, sampling accounts as they posted.",
        "Narrowed to **8,373 English-language posts from 344 accounts**, each holding between 50 and 50,000 followers.",
        "Every post **at least 48 hours old**, so engagement had largely finished accruing.",
        "Engagement counted as likes plus reposts plus replies, normalised per 1,000 followers.",
        "All public, unauthenticated endpoints. No PostDaily customer data was used, and none of it would have been enough — we are new.",
      ],
    },
    { type: "h2", text: "First, the answer everyone else publishes" },
    {
      type: "p",
      text: "Pool every post together, group by the hour it went out, and take the median engagement per 1,000 followers. Do that and you get a clear-looking result: the best hour outperforms the worst by **2.9x**, with a pronounced peak in the 05:00–09:00 UTC window.",
    },
    {
      type: "p",
      text: "That is a publishable chart. It is also wrong, and the way it is wrong is the interesting part.",
    },
    { type: "h2", text: "Why that number is an illusion" },
    {
      type: "p",
      text: "Different accounts post at different hours. The accounts that happen to post at 08:00 UTC are not the same accounts that post at 21:00 UTC — different people, different countries, different follower counts, different subject matter, different baseline engagement.",
    },
    {
      type: "p",
      text: "So when the pooled chart says 08:00 is a good hour, it cannot distinguish between two explanations: *posting at 08:00 helps*, or *accounts that post at 08:00 are doing better anyway*. Pooled data cannot tell those apart, and almost every article on this subject reports the first while measuring the second.",
    },
    {
      type: "quote",
      text: "A best-time chart built by pooling accounts is a chart of who posts when, wearing a chart of what time does as a disguise.",
    },
    { type: "h2", text: "The fix: compare each account against itself" },
    {
      type: "p",
      text: "Take one account. Find the median engagement across all its posts. Then ask, for each of its posts: did this one beat that account's own typical post?",
    },
    {
      type: "p",
      text: "Now every comparison is internal. A large account is compared only to itself, and a small one only to itself. Whatever makes an account generally successful — its subject, its audience, its follower count — cancels out, because it is present on both sides of the comparison. What is left is the thing we actually want to measure.",
    },
    {
      type: "p",
      text: "Across the whole sample, **39.2% of posts beat their own account's median.** (Not 50%, because posts landing exactly on the median count as neither above nor below.) If the hour of day matters, good hours should sit well above 39.2% and bad hours well below it.",
    },
    { type: "h2", text: "What we found" },
    {
      type: "p",
      text: "They do not. Of the 24 hours and 7 weekdays tested — 31 buckets in total — **exactly one was statistically distinguishable from the baseline**: 08:00 UTC, at 48%.",
    },
    {
      type: "p",
      text: "And that one is almost certainly noise. Testing 31 buckets at 95% confidence, you expect roughly one or two false positives by chance alone. Finding exactly one is what pure randomness looks like.",
    },
    {
      type: "ul",
      items: [
        "**Hours ranged from 33% to 48%**, against a 39.2% baseline — and every confidence interval bar one overlapped it.",
        "**Not one day of the week differed.** Monday through Sunday all landed between 38% and 41%. The weekend effect people describe is not in this data.",
        "**The 2.9x pooled spread collapsed** to a difference we cannot distinguish from chance.",
      ],
    },
    { type: "h2", text: "What we are not saying" },
    {
      type: "p",
      text: "Not that timing is irrelevant. Absence of a detectable effect in one sample is not proof of no effect, and there are real limits here — worth stating plainly rather than in a footnote:",
    },
    {
      type: "ul",
      items: [
        "**We measured UTC, not local time.** We cannot know each author's timezone from public data. English Bluesky spans the US, UK and Australia, so genuine local-time effects would be partly smeared across the UTC clock.",
        "**Accounts between 50 and 50,000 followers.** Very large accounts have different dynamics, and accounts under 50 followers have engagement too close to zero to measure.",
        "**Active accounts.** Sampling from the firehose finds people who post often. Occasional posters may behave differently.",
        "**45-day window, English only.** A seasonal or language-specific effect would not show up.",
        "**Your audience is not the average audience.** A finding about 344 accounts says little about your particular followers.",
      ],
    },
    { type: "h2", text: "Why this might be true anyway" },
    {
      type: "p",
      text: "There is a mechanism that makes the null result unsurprising, and it is specific to Bluesky.",
    },
    {
      type: "p",
      text: "The default Following feed is **chronological**. On a ranked feed, timing compounds: an algorithm notices early engagement and shows the post to more people, so a good slot is amplified and a bad one is buried. That compounding is what makes timing matter so much on algorithmic networks — and it is exactly what Bluesky's default feed does not do.",
    },
    {
      type: "p",
      text: "On a chronological feed, your post is shown to your followers in order. Some are looking, some are not. Whether it is 9am or 9pm changes which of them are awake, and the effect is real but modest — nothing amplifies it into a large difference.",
    },
    { type: "h2", text: "So what should you actually do" },
    {
      type: "ul",
      items: [
        "**Stop optimising the hour.** On this evidence it is close to the lowest-value variable available to you.",
        "**Post more, and consistently.** Frequency and sustained presence swamp timing. A post that exists at a mediocre hour beats a perfect slot you never filled.",
        "**Space posts out** rather than batching them. Not because certain hours are magic, but because your followers are not all reading at the same moment and a chronological feed gives them one pass.",
        "**Watch your own data**, not an industry average. If any timing effect exists for you, it is specific to your audience and only your own posts can reveal it.",
        "**Spend the saved attention on replies.** They reach other people's audiences, which is the growth mechanism a chronological network actually has.",
      ],
    },
    { type: "h2", text: "Method, in full" },
    {
      type: "p",
      text: "Accounts were sampled from Bluesky's Jetstream firehose as they created posts — a sample of active posters that is unbiased with respect to engagement, which is the variable under test. For each sampled account we pulled the public profile for a follower count and up to 50 recent posts.",
    },
    {
      type: "p",
      text: "Reposts and replies were excluded: a repost carries someone else's timing, and replies have entirely different engagement dynamics. Posts newer than 48 hours were excluded, because engagement accrues and including them would measure recency instead of timing.",
    },
    {
      type: "p",
      text: "Confidence intervals came from a bootstrap resampling **accounts**, not posts. Posts from one account are not independent observations, and resampling posts would treat one prolific account as many pieces of evidence.",
    },
    {
      type: "p",
      text: "We tried the median of each post's ratio to its account's median first and discarded it: over a thousand posts sit exactly on their account's median, so the statistic sticks at 1.00 and hides real variation. The mean was worse — a single viral post dragged one hour to 9x. Share-above-own-median is bounded, robust to outliers, and cannot be moved by one lucky post.",
    },
    {
      type: "p",
      text: "If you want to check this for your own account rather than trust an average, that is the more useful exercise — [we built a free tool that runs this exact method against one handle](/tools/bluesky-best-time), and [the analytics guide](/blog/bluesky-analytics-what-you-can-measure) covers what else Bluesky will and will not tell you.",
    },
  ],
};

/**
 * The Mastodon companion to the Bluesky timing study — and deliberately NOT a
 * study. It reasons from measured Bluesky data plus Mastodon's structure, and
 * says so in the first paragraph and again at the end.
 *
 * We did not collect Mastodon data and the article explains why at length: many
 * instances prohibit research collection in their terms, the ethical norm in
 * the fediverse is consent from instance admins even for public posts, and
 * there is precedent — the Zignani et al. content-warnings dataset drew an open
 * letter from the community and was retracted from the Harvard Dataverse.
 *
 * That section is not throat-clearing. It is the part most likely to earn a
 * link from the audience this page is aimed at, who are unusually alert to
 * being harvested.
 */
const MASTODON_TIMING: BlogPost = {
  slug: "best-time-to-post-on-mastodon",
  title: "The best time to post on Mastodon (and why we did not measure it)",
  excerpt:
    "We ran a real study on Bluesky and found posting time barely mattered. Here is what that implies for Mastodon, and why we did not build the same dataset here.",
  category: "Playbooks",
  author: POSTDAILY,
  date: "2026-08-31",
  read: "8 min read",
  imageAlt:
    "Independent community servers and message cards arranged around clocks showing different times",
  hue: 150,
  body: [
    {
      type: "p",
      text: "This article is **reasoning, not measurement**, and it is worth saying that before anything else rather than in a footnote at the bottom.",
    },
    {
      type: "p",
      text: "We collected a real dataset for Bluesky and found that posting time barely mattered. We deliberately did not build the equivalent for Mastodon. What follows is what the Bluesky result implies here, which parts of the argument get stronger, and the one place on Mastodon where timing might genuinely matter.",
    },
    { type: "h2", text: "What we actually measured, on Bluesky" },
    {
      type: "p",
      text: "We sampled Bluesky's public firehose and analysed 8,373 English posts from 344 accounts. Pooled across accounts, the data showed a 2.9x spread between the best and worst hour — the sort of number these articles usually print.",
    },
    {
      type: "p",
      text: "That number turned out to be an artefact of *which* accounts post at which hours. Scoring every post against its own account's median instead removed the confounder, and the effect very nearly vanished: of 31 hour and weekday buckets, exactly one differed from baseline, which is what testing 31 buckets at 95% confidence produces by chance. [The full study is here](/blog/best-time-to-post-on-bluesky), method included.",
    },
    { type: "h2", text: "Why the argument is stronger on Mastodon" },
    {
      type: "p",
      text: "The mechanism behind that null result is the chronological feed. On a ranked network, timing compounds: the algorithm notices early engagement and shows the post to more people, so a good slot gets amplified into a large difference. Bluesky's default Following feed does not do that, and the effect duly failed to appear.",
    },
    {
      type: "p",
      text: "Mastodon's home timeline is chronological too — and unlike Bluesky, there is no algorithmic alternative sitting alongside it. Bluesky has custom feeds, which are a real discovery surface even if they are opt-in. Mastodon's home timeline has no ranking at all.",
    },
    {
      type: "quote",
      text: "If timing did not measurably matter on the network that has some amplification available, it is unlikely to matter more on the one that has less.",
    },
    {
      type: "p",
      text: "That is the whole inference, and it is worth being clear about its strength: it is an argument from mechanism, not a measurement. It could be wrong.",
    },
    { type: "h2", text: "The one place timing might genuinely matter here" },
    {
      type: "p",
      text: "Mastodon is not entirely unranked. The **Explore** tab surfaces trending posts, hashtags and links, and trending is computed from boosts over a recent window.",
    },
    {
      type: "p",
      text: "That is a cascade mechanism, and cascades are exactly where timing effects live. A post that picks up boosts quickly can reach Explore, and reaching Explore produces more boosts. If posting when more of your followers are awake makes early boosts more likely, that could matter in a way it does not on the home timeline.",
    },
    {
      type: "p",
      text: "We have not measured this and we are not going to assert it. It is the honest place to look if someone does run the study — and it is a much narrower claim than the ones usually made.",
    },
    { type: "h2", text: "What actually decides who sees a Mastodon post" },
    {
      type: "p",
      text: "Four things, none of which is the hour on the clock:",
    },
    {
      type: "ul",
      items: [
        "**Boosts.** The only real amplification path. A post that gets boosted by someone with a different audience reaches people you could never reach directly, and that is essentially the entire growth mechanism.",
        "**How busy your instance's local timeline is.** On a large server, posts scroll past quickly. This is timing-adjacent, but it is about which server you are on rather than which hour you post.",
        "**Whether you show up otherwise.** Replies and boosts of other people's work put you in front of their followers. On a network with no algorithm, being present is the distribution.",
        "**Content warnings and alt text.** Not engagement tricks — conventions. A post ignoring them gets fewer boosts here in a way it would not elsewhere.",
      ],
    },
    { type: "h2", text: "Why we did not build a Mastodon dataset" },
    {
      type: "p",
      text: "We could have. Public timeline endpoints exist and collecting from them is technically trivial. We decided not to, and the reasons are specific rather than squeamish.",
    },
    {
      type: "p",
      text: "**Many instances prohibit it in their terms.** Servers are independently governed, and a good number explicitly forbid collecting posts for research or analysis. A crawler that ignores instance policy is not gathering public data — it is breaking the rules of the community it is studying.",
    },
    {
      type: "p",
      text: '**The ethical norm here is higher than "it was public".** In fediverse research the expected standard is consent from instance administrators, even for publicly visible posts. That is a stricter bar than most social-media research applies, and it is the bar this community has set for itself.',
    },
    {
      type: "p",
      text: "**There is precedent.** A study analysing content warnings on Mastodon drew an open letter from the community and its dataset was withdrawn from the Harvard Dataverse. Researchers with more standing than a scheduling company have got this wrong and paid for it.",
    },
    {
      type: "p",
      text: "Set against that: we would have produced a blog post. Bluesky's firehose is a deliberate, documented public broadcast with different norms around it, which is why that study exists and this one does not.",
    },
    { type: "h2", text: "What to do instead" },
    {
      type: "ul",
      items: [
        "**Do not optimise the hour.** On the available evidence it is among the lowest-value things you could spend attention on.",
        "**Post consistently.** A post that exists at a mediocre time beats a perfect slot you never filled.",
        "**Spread posts out.** Not because certain hours are magic, but because a chronological timeline gives your followers one pass and they are not all reading at once.",
        "**Reply and boost.** On a network with no algorithm, that is the distribution mechanism. It is not a supplement to posting; it is most of the job.",
        "**Write content warnings and alt text.** They affect whether people boost you, which is the thing that actually determines reach here.",
      ],
    },
    { type: "h2", text: "Checking it for your own account" },
    {
      type: "p",
      text: "Our finding is about 344 Bluesky accounts. Yours is one account with one audience, and the only data that can answer the question for you is your own.",
    },
    {
      type: "p",
      text: "Vary your posting times deliberately for a few weeks, record what each post got, and compare posts against your own typical post rather than against an industry benchmark. That last part is the entire methodological trick from the Bluesky study, and it is what stops you concluding that Tuesdays are magic because you happened to post something good on one.",
    },
    {
      type: "p",
      text: "If you would rather set the rhythm once and stop thinking about it, that is what a [posting queue is for](/mastodon-scheduler) — and the honest pitch is consistency, not a secret about what time it is.",
    },
  ],
};

/**
 * The featured slot on /blog. The timing study leads because it is the only
 * piece here carrying original data — the one thing on the site someone would
 * link to without being asked.
 */
/**
 * C6: the hub guide for "how to schedule posts on facebook" (~6,600 searches a
 * month across the cluster). Every native step was read from Meta's help
 * centre on 2026-09-27: Page scheduling runs 20 minutes to 29 days ahead
 * (help/389849807718635), Group admins and moderators schedule in the Group
 * (help/124331564818285), and Meta documents no scheduling for profiles, pro
 * mode included (business/help/2680340558863560).
 */
const FACEBOOK_SCHEDULING_GUIDE: BlogPost = {
  slug: "how-to-schedule-facebook-posts",
  title: "How to schedule Facebook posts: Pages, Reels, Stories and Groups",
  excerpt:
    "Schedule a Facebook Page post up to 29 days ahead, find it again, and know what can't be scheduled at all, with Meta's own steps for each.",
  imageAlt:
    "A photo post, a vertical video and a round Story badge standing on a calendar board, with an alarm clock beside it",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-09-27",
  read: "7 min read",
  hue: 225,
  body: [
    {
      type: "p",
      text: "**You can schedule Facebook posts on a Page, up to 29 days ahead, for free, from Meta Business Suite.** Group admins and moderators can schedule posts inside a Group too. What nobody can schedule is a post to a personal profile from an outside tool: Facebook's publishing API only works with Pages. The steps below are Meta's own, checked against its help centre on 27 September 2026.",
    },
    { type: "h2", text: "Schedule a Page post on a computer" },
    {
      type: "ul",
      items: [
        "Switch to the Page you want to post as, then click **Meta Business Suite** in the left menu.",
        "Click **Create post** below the Page's profile picture and add your text, photos or video.",
        "Next to **Scheduling options**, turn on **Set date and time** and choose when it should go out. **Active times** suggests times from when your followers were most active over the past week.",
        "Click **Schedule**.",
      ],
    },
    {
      type: "p",
      text: "Meta allows a scheduled time **between 20 minutes and 29 days away**, and the time is read in your own current time zone. Anyone with Facebook access or task access to the Page can create and edit scheduled posts.",
    },
    {
      type: "p",
      text: "Starting in Business Suite itself works the same way: **Create post**, pick the Page under **Post to**, write the post, set the time under **Scheduling options**, then **Schedule**. That route lets you post to the Page and its linked Instagram account at once, with different text for each.",
    },
    { type: "h2", text: "Schedule from your phone" },
    {
      type: "p",
      text: "The Meta Business Suite app schedules posts, Reels and Stories too. Tap the **+** button, choose what you are making, and choose to schedule it instead of publishing now. Meta's own instructions for scheduling a Page post all go through Business Suite, on the web or in its app, so that is the place to look if you can't find the option elsewhere.",
    },
    { type: "h2", text: "Personal profiles, including professional mode" },
    {
      type: "p",
      text: "Meta's help centre documents scheduling for Pages and for Groups. It says nothing about scheduling on a profile, and its page on professional mode for profiles doesn't list scheduling among the tools it adds. Outside tools are simpler still: Facebook's API publishes to Pages only, so **no third-party scheduler can post to a profile**, whatever its pricing page implies. If you post as yourself and want to schedule, a Page is the way.",
    },
    { type: "h2", text: "Reels and Stories" },
    {
      type: "p",
      text: "Both can be scheduled on a Page. A Reel is one video; a Story is one photo or video that disappears after 24 hours, and a Story scheduled by an app carries no stickers, links or music. The step-by-step guides are [How to schedule Facebook Reels](/help/schedule-facebook-reels) and [How to schedule Facebook Stories](/help/schedule-facebook-stories).",
    },
    { type: "h2", text: "Groups" },
    {
      type: "p",
      text: "Admins and moderators can schedule a Group post from the Group itself: start typing a post, click the calendar icon next to **Post**, pick the time and click **Schedule**. The icon only appears once you have started typing, and only for admins and moderators. Admins can also set up recurring posts through **Admin Assist**.",
    },
    {
      type: "p",
      text: "No outside scheduler can do this any more: Meta removed the Groups publishing permission from every API version on 22 April 2024. [Scheduling Facebook Group posts](/blog/schedule-facebook-group-posts) covers what still works.",
    },
    { type: "h2", text: "Find, edit or cancel a scheduled post" },
    {
      type: "ul",
      items: [
        "**On a Page:** in Meta Business Suite, open **Content**, then the **Scheduled** tab under **Posts and reels** (Stories have their own tab). The **Planner** shows the same posts on a week or month calendar.",
        "**Change it:** open the post's **...** menu to edit, reschedule, duplicate, move it to drafts or delete it. Moving it to drafts cancels the time without losing the post.",
        "**In a Group:** open the Group, choose **Scheduled posts** under **Admin tools** (you may need to click **Manage** first), then reschedule, post now, edit or delete.",
      ],
    },
    { type: "h2", text: "When the native scheduler isn't enough" },
    {
      type: "p",
      text: "Business Suite is free and fine if Facebook and Instagram are all you post to, a month at a time. The limits show up at the edges: 29 days is the furthest ahead it goes, each post is placed by hand, and a scheduled post that fails tells you little about why.",
    },
    {
      type: "p",
      text: "A scheduler earns its place when you post to other networks from the same plan, want a queue that fills your usual posting times, want a first comment published straight after the post, or need a client to approve posts without a Facebook login. PostDaily schedules up to 12 months ahead and records the Page's own error when something is refused. [The Facebook scheduler](/facebook-scheduler) lists exactly what it can post.",
    },
    { type: "h2", text: "If a scheduled post didn't go out" },
    {
      type: "p",
      text: "Start with the Scheduled tab and your time zone: a post set in one zone and checked in another looks an hour or more late. For everything else, the causes and fixes are in [Why your Facebook scheduled post didn't post](/blog/facebook-scheduled-post-not-posting), and the causes that apply to every network are in [why scheduled posts fail](/blog/why-scheduled-posts-fail).",
    },
  ],
};

/**
 * C4: symptom-first troubleshooter. The Page facts are Meta's own
 * (help/389849807718635, help/124331564818285, checked 2026-09-27); the
 * error-code advice is `lib/provider-error-guidance.ts`, which the product uses
 * too, so the article and the in-app notice can't disagree.
 */
const FACEBOOK_POST_NOT_POSTING: BlogPost = {
  slug: "facebook-scheduled-post-not-posting",
  title: "Facebook scheduled post didn't post? Every cause, and the fix",
  excerpt:
    "The post vanished, the schedule button is missing, or a scheduler says your Page needs attention. What each symptom means and what actually fixes it.",
  imageAlt:
    "A post card with an orange warning badge on a pedestal, beside a calendar, a clock, a magnifying glass and an unplugged cable",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-09-27",
  read: "7 min read",
  hue: 250,
  body: [
    {
      type: "p",
      text: "**Most Facebook posts that \"didn't post\" were scheduled for a different time, moved, or blocked by a permission, and each of those leaves a clue.** Start from what you are seeing below. Where a fix depends on Meta, this says so, and where reconnecting a scheduler won't help, it says that too.",
    },
    { type: "h2", text: "The post isn't on the Page" },
    {
      type: "ul",
      items: [
        "**Check the time zone first.** Meta reads a scheduled time in the scheduler's own current time zone. A post set while travelling, or by a colleague elsewhere, goes out at their time, not yours.",
        "**Check where it went.** In Meta Business Suite, open **Content**, then **Posts and reels**, and look at **Scheduled**, **Drafts** and **Published**. Someone can reschedule a post or move it to drafts, which cancels its time without deleting it.",
        "**Check your access.** Only people with Facebook access or task access to the Page can create and edit scheduled posts. If your access was removed, the post may still exist while you can no longer see it.",
      ],
    },
    { type: "h2", text: "The schedule option is missing" },
    {
      type: "ul",
      items: [
        "**You're on a profile, not a Page.** Meta documents scheduling for Pages and Groups, and outside tools can only publish to Pages. Switch to the Page, or create one.",
        "**You're in a Group but not an admin or moderator.** In a Group, the calendar icon next to **Post** appears only for admins and moderators, and only after you start typing.",
        "**The time is outside Meta's window.** A Page post can be scheduled between 20 minutes and 29 days ahead. Anything sooner or later is refused.",
        "**You're looking in the wrong place.** Meta's steps for scheduling a Page post all go through Meta Business Suite, on the web or in its own app. Open the Page, click **Meta Business Suite**, then **Create post**.",
      ],
    },
    {
      type: "h2",
      text: "A third-party scheduler says the Page needs attention",
    },
    {
      type: "p",
      text: "Schedulers publish through Facebook's API with a permission you granted when you connected the Page. When that permission stops working, posts can't go out until it is fixed, and the reason is usually in the error Meta returns. Meta tags these errors with a number, and the number decides whether reconnecting helps:",
    },
    {
      type: "ul",
      items: [
        "**Reconnecting fixes it:** the app was removed from your Facebook account (458), your Facebook password changed (460), the session expired (463), or Meta rejected the stored session (467).",
        "**Reconnecting won't fix it — do this first:** Facebook put a security checkpoint on the account (459), so complete the checks on facebook.com; the account isn't confirmed (464), so confirm it at facebook.com; the account has no admin role on the Page (492), so ask a Page admin to grant one. Then reconnect.",
      ],
    },
    {
      type: "p",
      text: 'That distinction matters because the usual advice is "reconnect", and for 459, 464 and 492 reconnecting simply fails again. PostDaily shows the fix for the specific number rather than a generic reconnect prompt.',
    },
    { type: "h2", text: "It was a Group post from a scheduler" },
    {
      type: "p",
      text: "No cloud scheduler can post into a Facebook Group any more: Meta removed the Groups publishing permission from every API version on 22 April 2024. A Group post has to be scheduled inside the Group by an admin or moderator. [Scheduling Facebook Group posts](/blog/schedule-facebook-group-posts) has the details.",
    },
    { type: "h2", text: "It was a Reel or a Story" },
    {
      type: "ul",
      items: [
        "**Reels** must be one video of 3 to 90 seconds when published through the API. A longer or shorter file is refused.",
        "**Stories** take one photo or video, and a video Story runs 3 to 60 seconds. Stickers, links and music can't be added to a Story published by an app.",
        "**A first comment on a Reel or Story doesn't exist.** Facebook takes first comments on feed posts only, so a scheduler that promised one on a Reel can't deliver it.",
      ],
    },
    { type: "h2", text: "How PostDaily handles a post Facebook refuses" },
    {
      type: "p",
      text: "Temporary errors, like Facebook being briefly unavailable or rate-limiting, are retried automatically, so a blip doesn't cost the post. A post Facebook refuses outright is not retried forever: it is marked failed with Facebook's own message, and you get a notification by email or in the app.",
    },
    {
      type: "p",
      text: "When a Page connection breaks, the posts queued behind it wait instead of failing one by one at their times; they show as **Waiting on reconnect** and carry on once the Page is fixed. The steps are in [When a post fails](/help/when-a-post-fails) and [A channel stopped working](/help/channel-stopped-working).",
    },
    { type: "h2", text: "Still stuck" },
    {
      type: "p",
      text: "If none of these fit, the causes that affect every network, from expired tokens to media the network rejects, are in [why scheduled posts fail](/blog/why-scheduled-posts-fail). For the whole scheduling workflow on a Page, start with [how to schedule Facebook posts](/blog/how-to-schedule-facebook-posts).",
    },
  ],
};

/**
 * C5: the definitive "can you schedule Facebook Group posts" answer. Group
 * scheduling steps are Meta's (help/124331564818285, checked 2026-09-27); the
 * API removal is Meta's v19 changelog: deprecated 2024-01-23, removed from all
 * versions 2024-04-22.
 */
const FACEBOOK_GROUP_SCHEDULING: BlogPost = {
  slug: "schedule-facebook-group-posts",
  title: "Can you schedule posts in a Facebook Group? What still works",
  excerpt:
    "Group admins and moderators can schedule posts inside the Group. Schedulers can't, since Meta removed the Groups API in April 2024. Here is what works instead.",
  imageAlt:
    "A group of figures in front of a noticeboard with one pinned post, beside a padlock on a locked connector",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-09-27",
  read: "6 min read",
  hue: 215,
  body: [
    {
      type: "p",
      text: "**Yes, if you're an admin or moderator of the Group: Facebook lets you schedule the post inside the Group itself.** What you can't do any more is schedule a Group post from Buffer, Hootsuite, PostDaily or any other cloud scheduler. Meta removed the permission those tools used on 22 April 2024, and nothing replaced it.",
    },
    { type: "h2", text: "Schedule a post in a Group you manage" },
    {
      type: "ul",
      items: [
        "Go to facebook.com/groups and open the Group.",
        "Click **Write something...** to start a post. You may need to click **Discussion** first.",
        "Write the post, then click the **calendar** icon next to **Post**. It only appears once you've started typing, and only for admins and moderators.",
        "Pick the date and time and click **Schedule**.",
      ],
    },
    {
      type: "p",
      text: "To change it later, open the Group and choose **Scheduled posts** under **Admin tools**; you may have to click **Manage** first. From there you can reschedule, post it now, edit it or delete it. These steps are from Facebook's help centre, checked on 27 September 2026.",
    },
    { type: "h2", text: "Recurring posts with Admin Assist" },
    {
      type: "p",
      text: "Admins, not moderators, can set a post to repeat daily, weekly or monthly. Open **Admin Assist**, click **Add** next to **Publish a custom post**, write it or pick a suggested prompt, choose how often and when it starts, then **Add to Admin Assist**. The admin who sets it up is the author, and each repeat appears in **Scheduled posts** 24 hours before it publishes. Another admin who wants to edit it has to delete it from Admin Assist and add it again.",
    },
    { type: "h2", text: "Why schedulers stopped posting to Groups" },
    {
      type: "p",
      text: "Until 2024, schedulers posted to Groups through Facebook's Groups API, using a permission called publish_to_groups. Meta deprecated the Groups API with version 19.0 of its Graph API in January 2024, and on 22 April 2024 removed it from every version, along with the permissions that went with it. Since then no app has had an official way to post into a Group.",
    },
    {
      type: "p",
      text: "Some tools still list Groups. Before paying for one, ask how it posts. If the answer isn't Meta's API, it is one of the workarounds below, with the risks they carry.",
    },
    { type: "h2", text: "Browser-extension auto-posters" },
    {
      type: "p",
      text: "The tools that still \"post to Groups\" mostly run inside your browser, filling in the Group's composer as your personal account, outside Meta's API. They work until Facebook changes its page or blocks the pattern, and when they stop, the account doing the posting is yours. We don't recommend them, and PostDaily doesn't offer one: it publishes only through the official APIs each network provides.",
    },
    { type: "h2", text: "What to do instead" },
    {
      type: "ul",
      items: [
        "**Schedule it in the Group.** If you're an admin or moderator, the native scheduler above is the reliable route, and it's free.",
        "**Use recurring posts** for the prompts and reminders you post every week.",
        "**Post to your Page, then share into the Group.** Schedule the Page post in advance, and share it to the Group by hand when it goes out.",
        "**Ask an admin.** If you post in a Group you don't run, send your post to an admin or moderator to schedule for you.",
      ],
    },
    { type: "h2", text: "What PostDaily does for Facebook" },
    {
      type: "p",
      text: "PostDaily schedules posts, Reels and Stories to Facebook Pages, with first comments, a queue and client approvals, up to 12 months ahead. It doesn't post to Groups or profiles, because Meta's API doesn't allow either. The full list is on [the Facebook scheduler page](/facebook-scheduler), and the Page workflow from start to finish is in [how to schedule Facebook posts](/blog/how-to-schedule-facebook-posts).",
    },
    {
      type: "p",
      text: "If a Group post you expected didn't appear, check the Group's **Scheduled posts** first, and whether your admin or moderator role changed. The other causes are covered in [why a Facebook scheduled post didn't post](/blog/facebook-scheduled-post-not-posting).",
    },
  ],
};

export const FEATURED_POST: BlogPost = BLUESKY_TIMING_STUDY;

/**
 * Pricing explainer (docs/seo/10, "Pricing explainers"). Figures from
 * buffer.com/pricing (its calculator, monthly and yearly), buffer.com/pricing.md
 * and support.buffer.com, read 2026-09-27. Where Buffer's own pages disagree
 * (the yearly discount, first-comment networks) the post says so rather than
 * picking one. Re-check monthly with the comparison page.
 */
const BUFFER_PRICING: BlogPost = {
  slug: "buffer-pricing",
  title: "Buffer pricing in 2026: what 3, 10 and 25 channels really cost",
  excerpt:
    "Buffer charges per channel, with lower rates past ten and a Team plan at double the rate. What 3, 10 and 25 channels cost, from its own calculator.",
  imageAlt:
    "A row of post tiles on a shelf, each with a price tag and a stack of coins that grows from left to right, beside a calculator",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-09-28",
  read: "6 min read",
  hue: 250,
  body: [
    {
      type: "p",
      text: "**Buffer charges per channel.** Essentials is $6 per channel a month, or $5 on yearly billing. Team is $12, or $10 yearly, and adds unlimited users and approvals. Past ten channels the rate drops, and there is a free plan for up to three channels. Every figure here comes from Buffer's own pricing page and calculator, read on 27 September 2026.",
    },
    { type: "h2", text: "Buffer's plans at a glance" },
    {
      type: "ul",
      items: [
        "**Free: $0.** Up to 3 channels, 10 scheduled posts per channel at a time, and one user. Buffer's help centre adds a lifetime limit of eight different channel connections on the free plan.",
        "**Essentials: from $6 per channel a month.** One user, unlimited scheduled posts (with a fair-use cap of 5,000 per channel), first comments, hashtag manager, reports and analytics.",
        "**Team: from $12 per channel a month.** Everything in Essentials plus unlimited team members, access levels, approval workflows and branded reports.",
      ],
    },
    { type: "h2", text: "The per-channel rate drops after ten" },
    {
      type: "p",
      text: "Buffer's machine-readable pricing page lists the monthly rate for each band of channels. The rate applies to the channels inside that band, so your first ten always cost the top rate:",
    },
    {
      type: "ul",
      items: [
        "**Channels 1 to 10:** $6 on Essentials, $12 on Team.",
        "**Channels 11 to 25:** $4 on both.",
        "**Channels 26 to 50:** $3 on both.",
        "**Channel 51 and up:** $1 on Essentials, $2 on Team.",
      ],
    },
    {
      type: "p",
      text: 'Yearly billing charges ten months for twelve; the toggle says "save 2 months". The FAQ on the same page calls it a 20% discount, but the calculator charges the ten-for-twelve price, which is about 17% off. Buffer does not publish a yearly rate for each band, so the yearly figures below are the calculator\'s totals.',
    },
    { type: "h2", text: "What it costs at 3, 10 and 25 channels" },
    {
      type: "ul",
      items: [
        "**One person, 3 channels:** the free plan covers it if ten queued posts per channel is enough. Otherwise Essentials, 3 × $6 = $18 a month, or $180 a year.",
        "**One person, 10 channels:** Essentials, 10 × $6 = $60 a month, or $600 a year.",
        "**One person, 25 channels:** Essentials, 10 × $6 + 15 × $4 = $120 a month, or $1,200 a year.",
        "**Three people, 10 channels:** Team, because Essentials has one user. 10 × $12 = $120 a month, or $1,200 a year. The number of people does not change the Team price; only channels do.",
        "**Three people, 25 channels:** Team, 10 × $12 + 15 × $4 = $180 a month, or $1,800 a year.",
      ],
    },
    {
      type: "p",
      text: "The pattern: Buffer is cheap at one to three channels, the first ten are the expensive ones, and the step from one person to two is the biggest jump on the page, because it doubles the rate on those first ten.",
    },
    { type: "h2", text: "What Team adds, and what approval looks like" },
    {
      type: "p",
      text: 'Approving drafts is only available on Team. Buffer\'s help centre describes it through user permissions: people are set to "Needs Approval" or "Full Access", and each user has their own Buffer login. Team members are unlimited, so adding a reviewer costs nothing extra once you are on Team, but a client still needs an account to approve.',
    },
    { type: "h2", text: "The small print" },
    {
      type: "ul",
      items: [
        "**Trial:** 14 days, on the Team plan, one per account. Buffer's help centre says accounts without a saved card move to the free plan when it ends.",
        "**Bulk upload:** CSV upload works on every plan: up to 10 posts per channel per upload on Free and 100 on paid plans, text and single-image posts only, and not for YouTube.",
        "**First comments:** Essentials and up. The pricing page names Instagram, Facebook and LinkedIn; Buffer's help centre lists Instagram and LinkedIn.",
        "**Currency and tax:** billed in US dollars wherever you are, plus VAT for UK consumers.",
        "**Nonprofits:** 50% off paid plans.",
        "**Last change:** Buffer's FAQ says its pricing was last updated in November 2025.",
      ],
    },
    {
      type: "p",
      text: "Buffer publishes to Facebook, Instagram, TikTok, LinkedIn, Threads, Bluesky, YouTube Shorts, Pinterest, Google Business Profile, Mastodon, X and Substack. Facebook groups and Instagram personal profiles are handled by notification, meaning a reminder to post by hand.",
    },
    { type: "h2", text: "Is Buffer worth it?" },
    {
      type: "p",
      text: "At one to three channels, for one person, yes: it is well made and priced fairly, and the free plan may be all you need. The per-channel model starts to hurt when you pass a handful of accounts, and it hurts fastest the day a second person needs access.",
    },
    {
      type: "p",
      text: "If you are sizing it against a per-seat tool, [Hootsuite's pricing worked out the same way](/blog/hootsuite-pricing) is the other half of the picture. [PostDaily vs Buffer](/alternatives/buffer) covers where Buffer is the better choice, and [moving from Buffer without losing your queue](/blog/moving-from-buffer) covers the switch if you make it. PostDaily plans include a channel allowance and seats at one price; current prices are on [the pricing page](/pricing).",
    },
  ],
};

/**
 * Pricing explainer (docs/seo/10, "Pricing explainers"). Every figure is from
 * hootsuite.com/plans, help.hootsuite.com and hootsuite.com/legal/payment-terms,
 * read 2026-09-28. Hootsuite publishes annual-billing prices only, so no monthly
 * figure is quoted. Re-check monthly with the comparison page.
 */
const HOOTSUITE_PRICING: BlogPost = {
  slug: "hootsuite-pricing",
  title: "Hootsuite pricing in 2026, and whether there's still a free plan",
  excerpt:
    "No free plan, $99 per user for up to 10 accounts, and approvals on the $399 tier. What Hootsuite costs at 3, 10 and 25 accounts, from its own plans page.",
  imageAlt:
    "Three figures on office chairs, each with a stack of coins, in front of a chart board, beside a gift box under a glass dome",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-09-28",
  read: "6 min read",
  hue: 230,
  body: [
    {
      type: "p",
      text: "**Hootsuite has no free plan.** Its cheapest plan, Standard, costs $99 per user per month on annual billing and connects up to 10 social accounts. There is a 14-day free trial that needs no card. Every figure below comes from Hootsuite's own plans page and help centre, read on 28 September 2026.",
    },
    { type: "h2", text: "Hootsuite's plans at a glance" },
    {
      type: "ul",
      items: [
        "**Standard: $99 per user per month.** Up to 10 social accounts.",
        "**Professional: $199 per user per month.** Unlimited social accounts. Hootsuite marks it as the most popular plan.",
        "**Advanced: $399 per user per month.** Unlimited accounts, plus approval workflows and team routing.",
        "**Enterprise: priced on request.** Listening, SSO and compliance tools.",
      ],
    },
    {
      type: "p",
      text: 'All four prices are for annual billing; the plans page says so in its footnote. Monthly billing exists, and Hootsuite says annual billing gives "a lower per-user monthly rate", but it does not publish the monthly figure. If you plan to pay month to month, expect more than the numbers here.',
    },
    { type: "h2", text: "Is there a free plan?" },
    {
      type: "p",
      text: "No. The plans page lists Standard, Professional, Advanced and Enterprise, and the only free option is the trial. The trial runs 14 days, needs no credit card, and Hootsuite says it will not charge you automatically when it ends. Two limits are worth knowing before you judge the product by it: posting is capped at 10 to 20 posts a day per organisation, and bulk scheduling is switched off until you pay.",
    },
    {
      type: "p",
      text: "Hootsuite does keep a set of free tools, such as caption, hashtag and bio generators. They are useful, but they are not a scheduler.",
    },
    { type: "h2", text: "What it costs at 3, 10 and 25 accounts" },
    {
      type: "p",
      text: "Hootsuite charges per person, not per account, so the account count barely matters until you pass ten. People are what multiply the bill. Worked from the annual prices:",
    },
    {
      type: "ul",
      items: [
        "**One person, 3 accounts:** Standard, $99 a month, or $1,188 a year.",
        "**One person, 10 accounts:** still Standard, $99 a month. Ten is the cap.",
        "**One person, 25 accounts:** Professional, $199 a month, or $2,388 a year. Standard stops at 10, and Hootsuite does not price extra accounts as an add-on; its help centre suggests removing one when you reach the limit.",
        "**Three people, 10 accounts:** Standard for each, 3 × $99 = $297 a month, or $3,564 a year.",
        "**Three people who need approvals:** Advanced for each, 3 × $399 = $1,197 a month, or $14,364 a year.",
      ],
    },
    {
      type: "p",
      text: "That last line is the one that surprises agencies. Post approval workflows are only included on Advanced and Enterprise, and the approver has to be a member of your Hootsuite organisation, which means a paid seat. Approvals from people outside the organisation can only be recorded after the fact, and only on Enterprise.",
    },
    { type: "h2", text: "What each step up buys" },
    {
      type: "ul",
      items: [
        "**Every plan:** the shared inbox, bulk scheduling of up to 350 posts from a CSV, 100 AI credits a month, 7 days of Quick Search, and benchmarking against 5 competitors.",
        "**Professional adds:** unlimited accounts, DM automations and auto-responses, AI smart replies, custom reports, 30 days of Quick Search, trend forecasting, and 20 competitors.",
        "**Advanced adds:** post approval workflows, internal comments, message assignment and routing, and the ability to suspend scheduled posts.",
        "**Enterprise adds:** Talkwalker listening with 13 months of history and alerts, SSO, compliance tools, and employee advocacy.",
      ],
    },
    {
      type: "p",
      text: "Hootsuite publishes to Facebook Pages, Instagram business and creator accounts, LinkedIn, Threads, X, TikTok, Bluesky, YouTube and Pinterest. Google Business Profile and WhatsApp are connected to the inbox only, and Mastodon is not on the list.",
    },
    { type: "h2", text: "The small print" },
    {
      type: "ul",
      items: [
        "**Refunds:** Hootsuite's payment terms say fees for paid services are non-refundable.",
        "**Cancelling:** cancellation takes effect at the end of the period you have paid for, and annual billing is an annual commitment.",
        "**Nonprofits:** Hootsuite offers up to 60% off, which it lists as $39 for Standard, $79 for Professional and $159 for Advanced, per user per month.",
      ],
    },
    { type: "h2", text: "Is Hootsuite worth it?" },
    {
      type: "p",
      text: "For a marketing team that answers customers from its social tool every day, often yes: the inbox, automations and reporting are where the money goes, and a publishing-only scheduler does not replace them. For a creator, a freelancer or a small team that mostly needs posts to go out on time, you are paying per person for a lot you will not open.",
    },
    {
      type: "p",
      text: "If you are weighing it against a per-channel model instead, [Buffer's pricing worked out the same way](/blog/buffer-pricing) is the useful comparison. And [PostDaily vs Hootsuite](/alternatives/hootsuite) covers where Hootsuite is the better choice and who should stay. PostDaily has a permanent free plan and paid plans with seats included; current prices are on [the pricing page](/pricing).",
    },
  ],
};

/**
 * The ChatGPT and Claude setup guides (docs/seo, AI agents cluster). They
 * target "schedule social media posts with ChatGPT / Claude", where most
 * results stop at "ask ChatGPT to write a caption, then paste it".
 *
 * Menu paths were checked against OpenAI's developer docs and Claude's help
 * centre on 2026-10-02, and change often: re-check both before editing. Every
 * claim about PostDaily's behaviour must match `ai-agents-data.ts`. ChatGPT's
 * directory listing is still in review, so the ChatGPT post must not call
 * PostDaily an official ChatGPT app until it is published.
 */
const CHATGPT_SCHEDULING: BlogPost = {
  slug: "how-to-schedule-social-media-posts-with-chatgpt",
  title: "How to schedule social media posts with ChatGPT",
  excerpt:
    "ChatGPT can't post to Instagram or TikTok by itself. Connected to a scheduler over MCP, it can. The setup, what to ask it, and where it still falls short.",
  imageAlt:
    "A felt chat bubble with typing dots, plugged by a braided blue cable into a small hub, sending post cards into a weekly calendar board beside an alarm clock",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-10-02",
  read: "6 min read",
  hue: 170,
  body: [
    {
      type: "p",
      text: "**ChatGPT on its own can write a post, but it can't publish or schedule one.** Instagram, TikTok, Facebook and the rest only accept posts from apps they have approved, signed in to your account. To schedule from ChatGPT, you connect it to a scheduling tool that offers an MCP server. ChatGPT then asks that tool to do the posting. The steps below use PostDaily, which is our product, but the same approach works with any scheduler that offers MCP.",
    },
    { type: "h2", text: "Why ChatGPT can't post for you on its own" },
    {
      type: "p",
      text: "Every network guards publishing behind its own API. An app has to be reviewed and approved, and then each person signs in to give it access to their account. ChatGPT has no approved access to Instagram or TikTok, and it doesn't hold your passwords. So the usual workflow is copy and paste: ChatGPT drafts a caption, you paste it into the network or into a scheduler, and you fix whatever it got wrong about that network's limits.",
    },
    {
      type: "p",
      text: "That last part is where a lot of time goes. A caption that fits Instagram is too long for X, Bluesky counts characters differently from Mastodon, and since December 2025 Instagram caps a post at five hashtags. ChatGPT doesn't know any of that unless something tells it.",
    },
    { type: "h2", text: "What MCP changes" },
    {
      type: "p",
      text: "MCP (Model Context Protocol) is the standard way AI apps connect to other software. A scheduler that runs an MCP server offers ChatGPT a set of actions, such as listing your channels, checking a post against each network's rules, scheduling it or reading your analytics. ChatGPT decides when to use them, based on what you ask.",
    },
    {
      type: "p",
      text: "Your social accounts stay connected to the scheduler, not to ChatGPT. ChatGPT never sees your Instagram password or the tokens the scheduler holds. It only gets the actions you allow, in the workspaces you choose.",
    },
    { type: "h2", text: "Set it up" },
    {
      type: "p",
      text: "PostDaily isn't in ChatGPT's app directory yet, so you add it with developer mode. OpenAI decides who can use developer mode, and availability can depend on your plan and your workspace's settings. These steps were checked on 2 October 2026:",
    },
    {
      type: "ul",
      items: [
        "In ChatGPT, open **Settings → Security and login** and turn on **Developer mode**.",
        "Open **Plugins**, select **+**, name it PostDaily and enter **https://mcp.postdaily.app/mcp** under **Connection**.",
        "When ChatGPT asks you to sign in, PostDaily opens. Choose **Full access**, **Drafts only** or **Read only**, tick the workspaces ChatGPT may use, and allow access.",
        "Start a new chat and add PostDaily from the tools menu.",
      ],
    },
    {
      type: "p",
      text: "The full walkthrough, with what to do if a menu has moved, is in [Connect PostDaily to ChatGPT](/help/connect-chatgpt). You need a PostDaily account with at least one channel connected. The free plan is enough to try it, and the connection itself costs nothing extra on any plan.",
    },
    { type: "h2", text: "What to ask it" },
    {
      type: "p",
      text: "Talk to it the way you'd brief a colleague. Name the networks, the timing and anything it should avoid. Some requests that work well:",
    },
    {
      type: "ul",
      items: [
        '**"List my PostDaily channels."** A good first message: it confirms the connection and shows ChatGPT what it can post to.',
        "**\"Write a post about our Saturday market stall for Instagram and Facebook, and add it to my queue.\"** It writes a version for each network, checks each one against that network's rules and puts it in the next free slot of each channel's posting schedule.",
        '**"Plan five posts for next week about our new opening hours and fill my empty slots."** It reads your queue first, so it fills gaps instead of stacking posts on top of each other.',
        '**"Turn this blog post into a Threads chain and a Bluesky thread."** Paste the text or a link. It splits the post into parts that fit each network\'s limit.',
        '**"What did best last month, and when should I post?"** It reads your analytics and your channels\' own best times, not generic advice.',
      ],
    },
    {
      type: "p",
      text: 'Give times with a time zone if you work across several. A time without one is read in your workspace\'s time zone, which is usually what you meant, but "9am" for a team split between London and New York needs saying.',
    },
    { type: "h2", text: "It asks before anything goes out right away" },
    {
      type: "p",
      text: "Scheduling and publishing are different here. ChatGPT can put posts into your queue without asking each time, because a scheduled post waits there and you can still edit or cancel it in PostDaily. Publishing a post immediately, or retrying one that failed, needs your go-ahead in the chat first, even with full access. If a post has warnings, for example a video the network may reject, ChatGPT has to show them to you before it can go ahead.",
    },
    {
      type: "p",
      text: "If you'd rather check everything yourself, connect with **Drafts only**. ChatGPT can then write and save drafts and upload media, but nothing is scheduled until you do it in PostDaily. In a workspace that requires client approval, posts that ChatGPT schedules wait for the client, exactly like posts you write yourself.",
    },
    { type: "h2", text: "Where it still falls short" },
    {
      type: "ul",
      items: [
        "**Files on your phone or computer.** ChatGPT can't hand a file from your device to PostDaily. Ask it for an upload link: it opens your PostDaily media library, you upload there, and it attaches what you added. Images at a public web address it can add directly.",
        "**What the networks don't allow.** No outside tool can post to a personal Facebook profile, only to Pages. Connecting ChatGPT doesn't change what a network's API permits.",
        "**Networks PostDaily doesn't publish to yet.** LinkedIn is still in progress. ChatGPT only sees the channels you have connected, so it won't pretend otherwise.",
        '**Your brief.** It writes from what you give it. A specific request (the offer, the audience, the tone, what not to say) gets a far better post than "write something about our product".',
      ],
    },
    { type: "h2", text: "Other AI apps work the same way" },
    {
      type: "p",
      text: "Nothing here is specific to ChatGPT. Any AI app that supports MCP can connect to the same server. Claude is the simplest to set up, because PostDaily is listed in its connector directory and works on every Claude plan: see [how to schedule social media posts with Claude](/blog/how-to-schedule-social-media-posts-with-claude). Coding agents such as Cursor, Codex and Claude Code can go one step further and upload a screenshot or a demo video straight from your project. The [AI agents](/ai-agents) page lists the apps and what each one can do.",
    },
  ],
};

const CLAUDE_SCHEDULING: BlogPost = {
  slug: "how-to-schedule-social-media-posts-with-claude",
  title: "How to schedule social media posts with Claude",
  excerpt:
    "Add a scheduler from Claude's connector directory, then ask Claude to write, schedule and check your posts on any plan. The setup, good prompts and the limits.",
  imageAlt:
    "An open felt notebook with chat bubbles on one page and a weekly calendar of post cards on the other, joined by a green connector, as a robot hand places one more card",
  category: "Guides",
  author: POSTDAILY,
  date: "2026-10-02",
  read: "6 min read",
  hue: 35,
  body: [
    {
      type: "p",
      text: "**Claude can schedule social media posts once you give it a connector to a scheduling tool.** Claude doesn't have access to your Instagram or TikTok account itself. A connector lets it ask a scheduler to post for you, signed in as you. PostDaily, which is our product, is listed in Claude's connector directory, so setup takes a couple of minutes on any Claude plan, Free included. Once connected, it works on the web, in Claude Desktop and in the Claude phone apps.",
    },
    { type: "h2", text: "What you need" },
    {
      type: "ul",
      items: [
        "**A Claude account.** Directory connectors work on every plan. If you add a connector by URL instead, Claude Free allows only one such custom connector, which is why the directory is the better route.",
        "**A PostDaily account with a channel connected**, such as an Instagram account or a Facebook Page. The free plan is enough to try this, and the connection costs nothing extra on any plan.",
      ],
    },
    { type: "h2", text: "Connect PostDaily to Claude" },
    {
      type: "ul",
      items: [
        "In Claude, open **Customize → Connectors** and select **+** next to Connectors.",
        "Find PostDaily in the directory and select **Connect**.",
        "PostDaily opens. Choose what Claude can do (**Full access**, **Drafts only** or **Read only**), tick the workspaces it may use and allow access.",
        "In a chat, select **+**, hover over **Connectors** and switch PostDaily on for that conversation.",
      ],
    },
    {
      type: "p",
      text: "These steps match Claude's help centre as of 2 October 2026. The full version, including Team and Enterprise plans where an Owner adds connectors for everyone, is in [Connect PostDaily to Claude](/help/connect-claude).",
    },
    { type: "h2", text: "A first session" },
    {
      type: "p",
      text: 'Start with "List my PostDaily channels." Claude replies with the accounts it can post to, which confirms the connection and shows you the names it will use.',
    },
    {
      type: "p",
      text: "Then give it something real: \"Write a post announcing our new autumn menu for Instagram and Threads. Friendly, no more than two emoji. Schedule it for Thursday at 11am.\" Behind the scenes, Claude writes a version for each network and checks them against each network's rules before saving anything: character limits counted the way that network counts them, Instagram's five-hashtag cap, which media each one accepts. Errors block the post. Warnings come back to Claude, which has to show them to you before it can go ahead.",
    },
    {
      type: "p",
      text: "The post then appears in your PostDaily queue and calendar like any other. You can open it there, change a word and leave the time as it is.",
    },
    { type: "h2", text: "Prompts worth keeping" },
    {
      type: "ul",
      items: [
        '**Weekly planning:** "Look at my queue for next week and fill the empty slots with posts about our workshop series. Mix tips, behind-the-scenes and one reminder to book."',
        '**Repurposing:** "Here\'s our latest newsletter. Turn it into a Bluesky thread, an Instagram caption and a short Facebook post, and queue them."',
        '**Reporting:** "Summarise how my posts did last month. Which three did best, and what do they have in common?"',
        '**Fixing failures:** "Did any posts fail this week? Explain why, and retry the ones that can be retried." Claude will ask before it retries.',
      ],
    },
    {
      type: "p",
      text: 'Claude remembers the conversation, so you can keep refining: "Make the second one shorter" or "Move all of these an hour later" works on the posts it just made.',
    },
    { type: "h2", text: "Photos and videos" },
    {
      type: "p",
      text: "In the Claude app, files you attach to the chat stay with Claude. To use your own images or video in a post, ask Claude for an upload link. It opens your PostDaily media library in the right workspace. Upload there, tell Claude you're done, and it attaches the files. In [Claude Code](/help/connect-claude-code), it can upload a file from your computer directly: just name the file.",
    },
    { type: "h2", text: "What Claude won't do" },
    {
      type: "ul",
      items: [
        "**Publish right away without asking.** Scheduling into your queue is fine, because the post waits there. Publishing immediately, or retrying a failed post, needs your go-ahead in the chat first, even with full access.",
        "**Skip your approvals.** If a workspace requires client approval, a post Claude schedules waits for the client like any other.",
        "**Go beyond what a network allows.** It can't post to a personal Facebook profile, for example, because Facebook only lets outside tools publish to Pages.",
        "**See more than you allowed.** It only sees the workspaces you ticked, and with Read only access it can look but not change anything.",
      ],
    },
    { type: "h2", text: "Turning it off" },
    {
      type: "p",
      text: "Switch the connector off in a chat when you don't need it. To revoke access completely, open **Settings → AI agents** in PostDaily and select **Disconnect** next to Claude. It stops working on its next request. The same page shows every connected app, which workspaces it can use and when it was last used.",
    },
    {
      type: "p",
      text: "If you also use ChatGPT, the same server works there, through ChatGPT's developer mode: see [how to schedule social media posts with ChatGPT](/blog/how-to-schedule-social-media-posts-with-chatgpt). The [AI agents](/ai-agents) page lists every app that can connect.",
    },
  ],
};

export const BLOG_POSTS: BlogPost[] = [
  CHATGPT_SCHEDULING,
  CLAUDE_SCHEDULING,
  BUFFER_PRICING,
  HOOTSUITE_PRICING,
  FACEBOOK_SCHEDULING_GUIDE,
  FACEBOOK_POST_NOT_POSTING,
  FACEBOOK_GROUP_SCHEDULING,
  MASTODON_TIMING,
  BLUESKY_SCHEDULING,
  MASTODON_SCHEDULING,
  BLUESKY_APP_PASSWORDS,
  MASTODON_CHARACTER_LIMIT,
  BLUESKY_THREADS,
  CROSS_POST_BLUESKY_MASTODON,
  HOW_OFTEN_BLUESKY,
  BLUESKY_ANALYTICS,
  WHAT_IS_THE_FEDIVERSE,
  BLUESKY_VS_MASTODON_VS_THREADS,
  MASTODON_PROJECT_ACCOUNT,
  WHY_SCHEDULED_POSTS_FAIL,
  CONTENT_CALENDAR,
  FREELANCER_APPROVALS,
  FACEBOOK_WITHOUT_BUSINESS_SUITE,
  MOVING_FROM_BUFFER,
];

/**
 * Written, reviewed-pending, not published.
 *
 * Drafts are deliberately kept out of `ALL_POSTS`, so they are not routed, not
 * prerendered, not in the sitemap and not reachable by URL. Publishing one is a
 * single move from this array into `BLOG_POSTS`.
 *
 * The reason this array exists rather than everything shipping at once: sixteen
 * articles appearing on one day, under one byline, is the scaled-content
 * pattern Google's spam policy describes, and it reads as machine-generated to
 * a human too. A fortnightly cadence is the plan; this is where the queue
 * waits. The `date` on each is the intended publish date, not the day it was
 * written — set it to the real date when you move it across.
 */
export const DRAFT_POSTS: BlogPost[] = [];

export const ALL_POSTS: BlogPost[] = [FEATURED_POST, ...BLOG_POSTS];

export function getPost(slug: string): BlogPost | undefined {
  return ALL_POSTS.find((post) => post.slug === slug);
}

/**
 * Derived, not declared. The chips on /blog are labels rather than filters, so
 * a hardcoded list advertises sections that contain nothing — which is exactly
 * what happened when the placeholder posts were removed and three of the four
 * categories emptied out.
 */
export const BLOG_CATEGORIES: BlogCategory[] = [
  ...new Set(ALL_POSTS.map((post) => post.category)),
];

/** Striped cover-placeholder background for a given hue. */
export function stripeBg(hue: number, size = 18): string {
  return `repeating-linear-gradient(135deg,oklch(0.93 0.035 ${hue}),oklch(0.93 0.035 ${hue}) ${size}px,oklch(0.96 0.022 ${hue}) ${size}px,oklch(0.96 0.022 ${hue}) ${size * 2}px)`;
}
